{
  "common": {
    "cancel": "Cancel",
    "dropdown_menu_title": "Choose {{label}}"
  },
  "seo": {
    "title": "Coval Flow — Event management platform",
    "description": "Plan events, coordinate volunteers, and manage contracts from a single workspace."
  },
  "notFound": {
    "title": "Page not found",
    "text": "The page you are looking for does not exist or has been moved.",
    "back": "Back to home"
  },
  "language": {
    "switcher_aria": "Switch language",
    "current": "en"
  },
  "nav": {
    "home": "Home",
    "events": "Events",
    "volunteers": "Volunteers",
    "contracts": "Contracts",
    "settings": "Settings",
    "section_main": "Manage",
    "finances": "Finances",
    "section_settings": "Settings",
    "profile": "Profile",
    "section_account": "Account",
    "organisation": "Organisation"
  },
  "auth": {
    "eyebrow": "Private access",
    "connected_as": "Connected as",
    "email_label": "Email",
    "password_label": "Password",
    "confirm_password_label": "Confirm password",
    "new_password_label": "New password",
    "confirm_new_password_label": "Confirm new password",
    "login_title": "Log in",
    "login_subtitle": "Access your workspace, profile, and organisation settings.",
    "login_submit": "Log in",
    "login_link_prefix": "Already have an account?",
    "login_link_action": "Log in",
    "register_title": "Create account",
    "register_subtitle": "Create your account and land directly in your dashboard.",
    "register_submit": "Create account",
    "register_link_prefix": "Need an account?",
    "register_link_action": "Register",
    "forgot_password_title": "Forgot password",
    "forgot_password_subtitle": "Enter your email to receive a reset link.",
    "forgot_password_link": "Forgot your password?",
    "send_reset_link": "Send reset link",
    "reset_email_sent": "If the account exists, a reset link is on its way.",
    "reset_password_title": "Reset password",
    "reset_password_subtitle": "Choose a new password for your account.",
    "reset_password_submit": "Reset password",
    "reset_token_missing": "The reset link is incomplete or expired.",
    "back_to_login": "Back to login",
    "logout": "Log out",
    "profile_title": "Profile",
    "invalid_credentials": "Invalid email or password",
    "password_too_short": "Password must be at least 8 characters",
    "password_min_hint": "Minimum {{min}} characters",
    "password_min_chars": "characters minimum",
    "password_mismatch": "Passwords do not match",
    "password_reset_success": "Password updated successfully",
    "full_name_label": "Full name",
    "full_name_placeholder": "Enter your full name"
  },
  "dashboard": {
    "eyebrow": "Control room",
    "welcome": "Welcome back, {{name}}",
    "subtitle": "Your account is ready. This dashboard will become the operational center for events, volunteers, and planning.",
    "organisation_title": "Active organisation",
    "organisation_summary": "{{name}} is connected and identified as @{{slug}}.",
    "organisation_missing": "No organisation is attached to your account yet.",
    "account_label": "Account",
    "role_label": "Role",
    "status_label": "Status",
    "status_ready": "Ready",
    "no_role": "Not assigned"
  },
  "profile": {
    "title": "Your profile",
    "subtitle": "Update your identity details and password from a single control panel.",
    "organisation_summary": "Connected to {{name}} with role {{role}}.",
    "organisation_missing": "No organisation information is currently available.",
    "email_label": "Email",
    "full_name_label": "Full name",
    "full_name_placeholder": "Enter your full name",
    "current_password_label": "Current password",
    "new_password_label": "New password",
    "confirm_new_password_label": "Confirm new password",
    "save": "Save changes",
    "eyebrow": "Account"
  },
  "onboarding": {
    "choice_title": "Welcome to Coval Flow",
    "choice_subtitle": "Create your organisation or join an existing one via an invitation link.",
    "choice_create_title": "Create my organisation",
    "choice_create_desc": "Set up your workspace and manage your events from your own organisation.",
    "choice_join_title": "Join an organisation",
    "choice_join_desc": "Received an invitation link? Paste it here to join the team.",
    "create_title": "Create your organisation",
    "create_subtitle": "Enter your Belgian company details to set up your workspace.",
    "join_title": "Join an organisation",
    "join_subtitle": "Paste the invitation link received by email to join the team.",
    "step_basic": "General",
    "step_legal": "Legal",
    "step_address": "Address",
    "name_label": "Organisation name",
    "name_placeholder": "e.g. City Lights Festival",
    "slug_label": "Public slug",
    "slug_placeholder": "city-lights-festival",
    "slug_hint": "Use a short, unique slug. Only lowercase letters, numbers, and dashes are kept.",
    "org_email_label": "Contact email",
    "phone_label": "Phone",
    "phone_placeholder": "+32 2 000 00 00",
    "website_label": "Website (optional)",
    "website_placeholder": "https://myorganisation.be",
    "legal_notice": "This information is used to identify your organisation with Belgian authorities.",
    "company_number_label": "Company number (BCE)",
    "company_number_invalid": "Expected format: 0XXX.XXX.XXX",
    "vat_number_label": "VAT number",
    "vat_number_invalid": "Expected format: BE0XXX.XXX.XXX",
    "vat_hint": "The VAT number is automatically derived from the BCE number.",
    "address_street_label": "Street",
    "address_street_placeholder": "Rue de la Loi",
    "address_number_label": "No.",
    "address_zip_label": "Postal code",
    "address_city_label": "City",
    "address_city_placeholder": "Brussels",
    "address_country_label": "Country",
    "join_instructions": "Copy and paste the full invitation link received in your email. The token will be extracted automatically.",
    "join_link_label": "Invitation link",
    "join_link_placeholder": "https://app.example.com/invitation?token=...",
    "join_token_not_found": "No invitation token detected in the link.",
    "join_error": "Invitation is invalid, expired, or already used.",
    "join_submit": "Join organisation",
    "back": "Back",
    "next": "Next",
    "submit": "Create organisation",
    "name_required": "Organisation name is required",
    "name_too_short": "Name must be at least 2 characters",
    "slug_required": "Slug is required",
    "slug_invalid": "Lowercase letters, numbers and dashes only (e.g. my-org)",
    "email_required": "Contact email is required",
    "email_invalid": "Invalid email format",
    "phone_required": "Phone number is required",
    "website_invalid": "URL must start with http:// or https://",
    "address_street_required": "Street is required",
    "address_number_required": "Number is required",
    "address_zip_required": "Postal code is required",
    "address_zip_invalid": "Expected format: 4 digits (e.g. 1000)",
    "address_city_required": "City is required",
    "address_country_required": "Country is required"
  },
  "invitation": {
    "eyebrow": "Invitation",
    "title": "Join this organisation",
    "subtitle": "Sign in or create your account to accept the invitation and join the team.",
    "create_account": "Create an account",
    "login": "Log in",
    "accepting": "Accepting invitation",
    "accept_error": "This invitation is invalid, expired, or has already been used.",
    "footer": "You will be redirected automatically once the invitation is accepted."
  },
  "settings": {
    "eyebrow": "Settings",
    "org_title": "Organisation settings",
    "org_subtitle": "Manage your organisation name and team members.",
    "nav": {
      "general": "General",
      "members": "Members",
      "invitations": "Invitations",
      "volunteer_link": "Volunteer link",
      "referentials": "Referentials",
      "finance": "Finance",
      "contract_templates": "Contract templates",
      "plan": "Plan"
    },
    "plan": {
      "current_plan": "Current plan",
      "subtitle": "What your organisation can do today, and where it stands.",
      "usage_title": "Your usage",
      "usage": {
        "active_events": "Events on your plan",
        "team_members": "Team members",
        "contract_signatures": "Contract signatures"
      },
      "of": "{{current}} of {{limit}}",
      "unlimited": "Unlimited",
      "unlimited_count": "{{current}} — unlimited",
      "volunteers_per_event": "Volunteers per event",
      "volunteers_hint": "Applied to each event on its own. An event can go further with its own package, without changing your plan.",
      "loading": "Loading your plan…",
      "error": "Your plan could not be loaded right now.",
      "covered_title": "Covered events",
      "covered_hint": "The packages you have bought, event by event.",
      "covered_tier": "{{tier}} — up to {{volunteers}} volunteers",
      "covered_until": "Full access until {{date}}",
      "covered_ended": "Full access ended on {{date}}",
      "usage_active_events_hint": "Events covered by their own package are not counted here."
    },
    "info_title": "Organisation info",
    "details_title": "Organisation details",
    "details_copy": "Update your organisation name and contact email. This address also receives the signed-contract confirmation emails from Documenso.",
    "signature_title": "Organisation signature",
    "signature_copy": "Draw the signature here so it can be reused inside contract templates through the {{organisation_signature}} variable.",
    "signature_save": "Save signature",
    "signature_remove": "Remove saved signature",
    "signature_clear_draft": "Clear draft",
    "signature_preview_label": "Signature preview",
    "signature_empty": "No signature has been saved yet.",
    "signature_hint": "Place {{organisation_signature}} in the contract template exactly where this image should appear.",
    "signature_edit": "Edit signature",
    "signature_saved": "Signature saved",
    "signature_removed": "Signature removed",
    "volunteer_signup_title": "Volunteer signup link",
    "volunteer_signup_copy": "Share this public link with volunteers so they can create their account for your organisation.",
    "volunteer_signup_status": "Status",
    "volunteer_signup_active": "Active",
    "volunteer_signup_inactive": "Inactive",
    "volunteer_signup_toggle": "Accept new signups",
    "volunteer_signup_link_label": "Public signup link",
    "volunteer_signup_copy_button": "Copy link",
    "volunteer_signup_regenerate": "Regenerate link",
    "invitations": {
      "title": "Team invitation links",
      "copy": "Create an invitation for a teammate, then copy and share the generated join link.",
      "email_label": "Invitee email",
      "email_placeholder": "name@company.com",
      "role_label": "Role",
      "role_member": "Member",
      "role_admin": "Admin",
      "submit": "Create invitation",
      "created": "Invitation created",
      "pending_title": "Pending invitations",
      "empty": "No pending invitations.",
      "expires_on": "Expires on {{date}}",
      "copy_button": "Copy invitation link",
      "event_access_label": "Event access",
      "event_access_all": "All events",
      "event_access_specific": "Specific events",
      "event_access_select_events": "Select the accessible events",
      "event_access_empty": "No events available.",
      "delete_aria": "Delete invitation for {{email}}",
      "delete_confirm_title": "Delete invitation",
      "delete_confirm_copy": "The invitation link for {{email}} will stop working immediately. This action cannot be undone.",
      "delete_confirm_button": "Delete invitation",
      "deleted": "Invitation deleted"
    },
    "members_title": "Members",
    "name_label": "Name",
    "email_label": "Organisation email",
    "slug_label": "Slug",
    "created_label": "Created",
    "role_label": "Your role",
    "save": "Save changes",
    "loading": "Loading…",
    "no_members": "No members found.",
    "member_email": "Email",
    "member_name": "Name",
    "member_joined": "Joined",
    "member_role": "Role",
    "members": {
      "role_owner": "Owner",
      "role_admin": "Admin",
      "role_member": "Member",
      "role_updated": "Member role updated",
      "event_access_column": "Event access",
      "access_all": "All events",
      "access_count": "{{count}} event(s)",
      "event_access_can_manage": "Can manage this event",
      "actions": "Actions",
      "edit": "Edit member",
      "edit_title": "Edit member",
      "updated": "Member updated",
      "can_view_amounts": "Sees amounts",
      "can_view_amounts_hint": "Artist fees and costs",
      "amounts_updated": "Access to amounts updated"
    },
    "referentials": {
      "role_types_title": "Role types",
      "role_types_copy": "Define the role types assignable during events.",
      "role_types_empty": "No role types yet.",
      "role_type_placeholder": "New role name…",
      "role_type_added": "Role type added",
      "role_type_backup_label": "Backup role (renfort) — filled only once every normal role is covered",
      "role_type_backup_badge": "Backup",
      "role_type_phase_label": "Phase",
      "role_type_phase_none": "None",
      "role_type_deleted": "Role type deleted",
      "role_type_archived": "Role type archived (used in existing events)",
      "role_type_updated": "Role type updated",
      "add": "Add",
      "delete": "Delete",
      "edit": "Edit",
      "save": "Save",
      "cancel": "Cancel"
    },
    "finance": {
      "vat_title": "VAT rates",
      "vat_copy": "Editable VAT rates applied to transactions.",
      "vat_empty": "No VAT rates yet.",
      "vat_label_placeholder": "Label (e.g. 21%)",
      "vat_rate_placeholder": "Rate",
      "vat_country_placeholder": "Country (optional)",
      "categories_title": "Categories",
      "categories_copy": "Categories to classify expenses and income.",
      "categories_empty": "No categories yet.",
      "category_placeholder": "New category name…",
      "category_type_expense": "Expense",
      "category_type_income": "Income",
      "add": "Add",
      "delete": "Delete",
      "added": "Entry added",
      "deleted": "Entry deleted",
      "updated": "Category updated",
      "edit": "Edit",
      "save": "Save",
      "cancel": "Cancel"
    },
    "volunteer_contract_title": "Volunteer reimbursement caps",
    "volunteer_contract_copy": "Configure the maximum allowed amounts for volunteer reimbursements. These values are injected into contract templates via the {{organisation_volunteer_reimbursement_max}} and {{organisation_km_rate}} variables.",
    "owner_function_label": "Representative function (e.g. President, Artistic Director)",
    "volunteer_reimbursement_max_label": "Daily flat-rate cap (€)",
    "volunteer_reimbursement_max_annual_label": "Annual flat-rate cap (€)",
    "volunteer_km_rate_label": "Kilometre rate (€/km)",
    "volunteer_contract_save": "Save caps",
    "volunteer_contract_saved": "Caps saved",
    "ticketing": {
      "title": "Ticketing",
      "copy": "Connect one or more ticketing platforms to track sales and orders from your events. Each API key is encrypted and never shown again.",
      "list_empty": "No ticketing platform configured",
      "add_title": "Add a platform",
      "provider_label": "Ticketing platform",
      "account_id_label": "Organizer ID",
      "api_key_label": "API key",
      "api_key_placeholder": "Paste your API key",
      "coming_soon": "Coming soon",
      "save": "Save",
      "test": "Test",
      "remove": "Remove",
      "saved": "Ticketing platform saved",
      "removed": "Ticketing platform removed",
      "test_ok": "Connection successful — {{count}} event(s) reachable",
      "test_failed": "Could not connect: check your API key"
    }
  },
  "camera": {
    "title": "Take a photo",
    "starting": "Starting the camera…",
    "capture": "Capture",
    "retake": "Retake",
    "use_photo": "Use this photo",
    "cancel": "Cancel",
    "preview_alt": "Captured photo preview",
    "error": "The camera is unavailable. Check the permissions and try again."
  },
  "volunteerApp": {
    "eyebrow": "Volunteer space",
    "loading": "Loading signup link...",
    "title": "Create your volunteer account",
    "login_title": "Volunteer login",
    "login_copy": "Find your shifts, documents, and latest updates from your organisation.",
    "login_submit": "Log in",
    "login_error": "Unable to log you in with these credentials.",
    "forgot_password_link": "Forgot your password?",
    "forgot_password_title": "Forgot password",
    "forgot_password_subtitle": "Enter your email to receive a reset link.",
    "send_reset_link": "Send reset link",
    "reset_email_sent": "If the account exists, a reset link is on its way.",
    "back_to_login": "Back to login",
    "reset_password_title": "Reset password",
    "reset_password_subtitle": "Choose a new password for your volunteer account.",
    "reset_password_submit": "Reset password",
    "reset_token_missing": "The reset link is incomplete or expired.",
    "new_password_label": "New password",
    "confirm_new_password_label": "Confirm new password",
    "password_mismatch": "Passwords do not match",
    "password_reset_success": "Password updated successfully",
    "first_name": "First name",
    "last_name": "Last name",
    "email": "Email",
    "phone": "Phone",
    "password": "Password",
    "confirm_password": "Confirm password",
    "password_hint": "Password must contain at least 8 characters",
    "confirm_password_hint": "Passwords must match",
    "step_account": "Account",
    "step_legal": "Legal info",
    "step_address": "Address",
    "account_section_title": "Login details",
    "account_section_copy": "Start with your basic details to create your access.",
    "continue": "Continue",
    "back": "Back",
    "intro_account_title": "Personal account",
    "intro_account_copy": "Email, password, and contact details.",
    "intro_admin_title": "Useful details",
    "intro_admin_copy": "Address, national number, and bank account if needed.",
    "intro_access_title": "Direct access",
    "intro_access_copy": "Then sign in to the volunteer space to follow your shifts.",
    "legal_section_title": "Legal and banking details",
    "legal_section_copy": "These details are required to cover you during your missions.",
    "legal_notice_title": "Why we ask for this",
    "legal_notice_body": "Your national number and IBAN are used to draw up your volunteer contract. This document is required by our insurer to cover you during your missions and, where applicable, to reimburse your expenses or pay statutory flat-rate allowances under Belgian law.",
    "gdpr_retention_title": "How we protect your data (GDPR)",
    "gdpr_retention_body": "Your data is processed only by your organisation, strictly for managing your volunteer engagement and meeting its legal obligations (insurance, accounting). It is kept only for as long as needed for these purposes, then deleted or archived in line with statutory retention periods. You can exercise your rights of access, rectification, restriction, portability or deletion at any time by contacting your organisation directly.",
    "address_section_title": "Postal address",
    "address_section_copy": "This address is used for your official documents and your insurance declaration.",
    "national_number": "National number",
    "address_street": "Street",
    "address_number": "Number",
    "address_zip": "Postal code",
    "address_city": "City",
    "address_country": "Country",
    "bank_account": "Bank account / IBAN",
    "first_name_placeholder": "Jane",
    "last_name_placeholder": "Doe",
    "email_placeholder": "jane.doe@example.com",
    "phone_placeholder": "+32 470 12 34 56",
    "phone_invalid": "Enter a valid phone number (not an email address).",
    "password_placeholder": "At least 8 characters",
    "confirm_password_placeholder": "Repeat your password",
    "national_number_placeholder": "00.00.00-000.00",
    "bank_account_placeholder": "BE00 0000 0000 0000",
    "address_street_placeholder": "Rue de la Loi",
    "address_number_placeholder": "16",
    "address_zip_placeholder": "1000",
    "address_city_placeholder": "Brussels",
    "address_country_placeholder": "BE",
    "submit": "Create my account",
    "already_account": "Already registered?",
    "go_to_login": "Go to login",
    "success_title": "Account created",
    "success_copy": "Your volunteer account is ready. You can now log in to continue.",
    "unavailable_title": "Signup unavailable",
    "unavailable_copy": "This signup link is invalid, expired, or disabled.",
    "dashboard_eyebrow": "Volunteer portal",
    "dashboard_title": "Hello {{name}}",
    "dashboard_copy": "This screen keeps your next shifts, confirmed schedules, and useful documents in one place.",
    "upcoming_copy": "Keep an eye on your next shifts and confirm your attendance as soon as you can.",
    "past_copy": "Find your previously completed assignments here.",
    "documents_copy": "All useful documents shared by the organisation are gathered here.",
    "profile_title": "My profile",
    "profile_copy": "Quick view of your details and activity.",
    "stat_upcoming": "Upcoming shifts",
    "stat_documents": "Documents",
    "stat_past": "Past shifts",
    "photo_import": "Import a photo",
    "photo_take": "Take a photo",
    "photo_remove": "Remove photo",
    "photo_invalid_type": "Please choose a PNG, JPEG, or WebP image.",
    "photo_too_large": "The image is too large (5 MB maximum).",
    "upcoming_title": "My shifts",
    "past_title": "Past shifts",
    "documents_title": "My documents",
    "documents_col_name": "Document",
    "documents_col_status": "Status",
    "documents_col_download": "Download",
    "documents_col_sign": "Sign",
    "no_upcoming": "No upcoming shift assigned yet.",
    "no_past": "No past shift to display.",
    "no_documents": "No document is available yet.",
    "document_default_type": "Document",
    "documents_download": "Download PDF",
    "documents_sign": "Sign",
    "documents_signed": "Already signed",
    "documents_sign_unavailable": "No signing link is available for this document yet.",
    "documents_download_unavailable": "The signed PDF will be available once this contract has been signed.",
    "status_pending": "Pending",
    "status_confirmed": "Confirmed",
    "status_cancelled": "Unavailable",
    "confirm_button": "Confirm my attendance",
    "confirmed_button": "Attendance confirmed",
    "cancel_button": "I can't make it",
    "cancel_modal_title": "Report unavailability",
    "cancel_modal_copy": "Explain the reason if useful. The organiser keeps the shift visible until it is processed.",
    "cancel_reason": "Optional message",
    "cancel_submit": "Send unavailability",
    "logout": "Log out",
    "no_email": "No email provided",
    "no_phone": "No phone provided",
    "nav_events": "Events",
    "nav_preferences": "Preferences",
    "nav_upcoming": "Upcoming",
    "nav_documents": "Documents",
    "nav_past": "Past",
    "events_page_copy": "Browse your organisation's events and register for those you want to join.",
    "events_empty": "No active events at the moment.",
    "event_register": "I want to join",
    "event_unregister": "Unregister",
    "event_unregister_confirm_title": "Unregister",
    "event_unregister_confirm_copy": "Are you sure you want to unregister from this event?",
    "event_unregister_confirm_button": "Confirm unregistration",
    "reg_status_pending": "Awaiting",
    "reg_status_approved": "Approved",
    "reg_status_rejected": "Rejected",
    "not_registered": "Not registered",
    "registration_col": "Registration",
    "availability_col": "Availability",
    "availability_locked": "Register first",
    "availability_missing": "Not set",
    "availability_ready": "Provided",
    "availability_edit": "Edit",
    "event_detail_back": "Back to events",
    "partner_title": "My shift partner",
    "partner_copy": "Pick another volunteer you'd like to work at the same time as. We'll try to schedule you together.",
    "partner_label": "Partner",
    "partner_placeholder": "Choose a partner",
    "partner_clear": "Remove partner",
    "partner_empty": "No other volunteers are registered for this event yet.",
    "guests_title": "Guest List",
    "guests_copy": "The people invited to this event. Mark them present as they arrive at the door.",
    "guests_empty": "No guests have been added to this event.",
    "guests_present_count": "{{present}}/{{total}} present",
    "guests_plus_ones": "+{{n}} guests",
    "guests_present": "Present",
    "guests_mark_present": "Check in",
    "guests_view": "Guest List",
    "guests_search_placeholder": "Search a guest",
    "guests_no_match": "No guest matches your search.",
    "pref_want": "Preferred",
    "pref_avoid": "Avoid",
    "pref_none": "No preference",
    "pref_remove": "Remove preference",
    "registrations_title": "My registrations",
    "registrations_copy": "Track your event registration requests.",
    "no_registrations": "No pending registrations.",
    "stat_registrations": "Registrations",
    "max_hours_label": "Max hours per event:",
    "max_hours_none": "no limit set",
    "max_hours_edit": "Edit",
    "max_hours_modal_title": "Set max hours",
    "max_hours_modal_copy": "Set the maximum hours you want to work during the main event phase. Setup and teardown days are counted separately. Leave empty to use the event limit.",
    "max_hours_save": "Save",
    "personal_info_label": "Personal information",
    "personal_info_edit": "Edit personal info",
    "personal_info_modal_title": "Edit your personal information",
    "personal_info_modal_copy": "Keep your contact and administrative details up to date so the organisation can reach you and prepare your paperwork.",
    "personal_info_identity": "Identity & contact",
    "personal_info_admin": "Administrative details",
    "personal_info_address": "Address",
    "personal_info_save": "Save",
    "delete_account_label": "Danger zone",
    "delete_account_hint": "Permanently delete your volunteer account and your personal data.",
    "delete_account": "Delete my account",
    "delete_account_modal_title": "Delete my account",
    "delete_account_warning": "This action is permanent and cannot be undone.",
    "delete_account_copy": "Your account will be closed immediately and you will be signed out on every device.",
    "delete_account_erased": "Erased: your name, email, phone, national number, bank account, address, photo and preferences.",
    "delete_account_kept": "Kept: the contracts you signed and their accounting entries, which the organisation must retain by law. They will no longer carry your name.",
    "delete_account_password_label": "Confirm with your password",
    "delete_account_password_placeholder": "Your password",
    "delete_account_password_invalid": "Incorrect password",
    "delete_account_confirm": "Delete permanently",
    "delete_account_success": "Your account has been deleted",
    "shift_col_event": "Event",
    "shift_col_schedule": "Schedule",
    "shift_col_status": "Status",
    "shift_confirm": "Confirm",
    "shift_decline": "Decline",
    "feedback_col": "Feedback",
    "feedback_leave": "Leave feedback",
    "feedback_edit": "Edit my feedback",
    "feedback_modal_title": "Event feedback",
    "feedback_modal_copy": "Share your experience about \"{{event}}\". Your comment helps the organisation improve.",
    "feedback_comment_label": "Your comment",
    "feedback_comment_placeholder": "What went well? What could be improved?",
    "feedback_submit": "Send feedback",
    "feedback_anonymous_label": "Leave this feedback anonymously",
    "feedback_anonymous_hint": "If enabled, the organisation will not see your name next to this comment.",
    "nickname": "Nickname",
    "nickname_placeholder": "e.g. Alex",
    "nickname_hint": "When set, your nickname replaces your name on the schedule.",
    "nickname_too_short": "Your nickname must be at least 4 characters.",
    "hero_nickname": "Nickname: {{nickname}}",
    "register_availability_title": "Your availability for {{event}}",
    "register_sent": "Request sent",
    "register_availability_intro": "Paint when you are available: your request is sent with it.",
    "register_submit": "Send my request",
    "register_cancel": "Cancel",
    "preferences_subtitle": "Click a role type to mark whether you want it or would rather avoid it."
  },
  "contact": {
    "dropdownPlaceholder": "Subject*"
  },
  "events": {
    "dashboard_eyebrow": "Operations board",
    "dashboard_title": "{{name}} events",
    "dashboard_subtitle": "Track every event from a single control surface: recruiting, contracts, and planning readiness stay visible at a glance.",
    "empty": "No events found for this organisation yet.",
    "actions": {
      "create": "Create event",
      "open": "Open event",
      "save": "Save event",
      "cancel": "Cancel",
      "edit": "Edit event",
      "activate": "Activate",
      "reactivate": "Reactivate",
      "complete": "Mark as completed",
      "complete_confirm": "Mark as completed",
      "duplicate": "Duplicate",
      "archive": "Archive",
      "archive_confirm": "Archive event",
      "activate_success": "Event activated successfully",
      "reactivate_success": "Event reactivated successfully",
      "complete_success": "Event marked as completed",
      "save_success": "Event saved successfully"
    },
    "filters": {
      "eyebrow": "Filter",
      "title": "Display scope",
      "label": "Status",
      "placeholder": "Choose a status",
      "all": "Visible events",
      "draft": "Draft",
      "active": "Active",
      "completed": "Completed",
      "archived": "Archived"
    },
    "stats": {
      "total_events": "Events",
      "active_events": "Active",
      "recruited_volunteers": "Recruited volunteers",
      "generated_planning": "Generated planning"
    },
    "labels": {
      "name": "Event",
      "dates": "Dates",
      "actions": "Actions",
      "roles": "Roles",
      "volunteers": "Volunteers",
      "contracts": "Contracts",
      "planning": "Planning",
      "planning_yes": "Yes",
      "planning_no": "No",
      "location_missing": "Location not set yet",
      "description_missing": "No event description yet.",
      "pending_badge": "{{count}} pending",
      "pending_hint": "Volunteer registrations awaiting your approval"
    },
    "form": {
      "eyebrow": "New event",
      "title": "Create a new event",
      "subtitle": "This event will be attached to {{name}} and become the main container for volunteers, roles, and planning.",
      "name": "Event name",
      "start_date": "Start date",
      "end_date": "End date",
      "location": "Location",
      "org_label": "Organisation",
      "description": "Description",
      "timeline_label": "Timeline",
      "duration_label": "Duration",
      "duration_single": "{{count}} day",
      "duration_plural": "{{count}} days",
      "date_error": "The end date must be later than the start date.",
      "auto_shift_hint": "If the start date catches up with the end date, the end date is pushed automatically to keep a valid range.",
      "preview_eyebrow": "Live preview",
      "preview_title": "Event snapshot",
      "preview_copy": "Review the event frame before saving it. This panel updates as you type.",
      "preview_name_placeholder": "Untitled event",
      "preview_ready": "Ready",
      "preview_pending": "To complete",
      "side_title": "Before you publish",
      "side_copy": "Start with the core framing. You can refine volunteers, role structure, and contracts afterward from the event detail page.",
      "sections": {
        "basics_eyebrow": "Identity",
        "basics_title": "Core information",
        "basics_copy": "Start with the public name and the place where the team will operate.",
        "schedule_eyebrow": "Dates",
        "schedule_title": "Event window",
        "schedule_copy": "Keep a clear start and end date so the planning and volunteer assignment stay consistent.",
        "context_eyebrow": "Context",
        "context_title": "Operational notes",
        "context_copy": "Add enough context now so the event remains readable when the dashboard fills up.",
        "windows_eyebrow": "Hours",
        "windows_title": "Daily time windows",
        "windows_copy": "Optionally set the active time window for each day. These windows define what is shown in the coverage timeline and can be adjusted later."
      },
      "hints": {
        "organisation": "The event is linked to the organisation currently selected in your workspace.",
        "dates": "Choose a clear start and end window so planning stays coherent.",
        "details": "Add location and context now to make the dashboard easier to scan later."
      }
    },
    "detail": {
      "eyebrow": "Event detail",
      "info_title": "Event information",
      "readonly": "Read-only"
    },
    "cover": {
      "add": "Add cover image",
      "change": "Change cover",
      "remove": "Remove cover",
      "empty": "No cover image yet",
      "alt": "Cover image for {{name}}",
      "modal_title": "Event cover image",
      "modal_copy": "Upload a PNG, JPEG or WebP image (max 5 MB). It is displayed with the event details.",
      "file_label": "Cover image",
      "save": "Save cover",
      "save_success": "Cover image updated successfully",
      "remove_success": "Cover image removed successfully"
    },
    "tabs": {
      "roles": "Roles",
      "volunteers": "Volunteers",
      "guestlist": "Guest List",
      "accounting": "Accounting",
      "contracts": "Contracts",
      "planning": "Planning",
      "feedback": "Feedback",
      "billetterie": "Ticketing",
      "prod": "Prod"
    },
    "billetterie": {
      "title": "Ticketing",
      "subtitle": "Live sales and orders pulled from your ticketing provider.",
      "not_configured_title": "No ticketing provider configured",
      "not_configured_copy": "Add your ticketing provider API key in the organisation settings to enable this tab.",
      "go_to_settings": "Open settings",
      "link_title": "Link a ticketing event",
      "link_copy": "Pick the provider event that matches this event to display its sales.",
      "provider_label": "Platform",
      "provider_unavailable": "The linked platform is no longer configured for this organisation.",
      "link_select_label": "Provider event",
      "link_button": "Link event",
      "link_empty": "No event is available for this API key.",
      "linked_label": "Linked event",
      "unlink_button": "Unlink",
      "refresh": "Refresh",
      "kpi_income": "Revenue",
      "kpi_tickets": "Tickets sold",
      "kpi_orders": "Orders",
      "tiers_title": "Ticket tiers",
      "tier_name": "Tier",
      "tier_price": "Price",
      "tier_sold": "Sold",
      "tier_available": "Available",
      "orders_title": "Orders",
      "order_customer": "Customer",
      "order_email": "Email",
      "order_status": "Status",
      "order_total": "Total",
      "order_date": "Date",
      "orders_empty": "No orders yet.",
      "linked_success": "Event linked successfully",
      "unlinked_success": "Event unlinked successfully"
    },
    "accounting": {
      "total_income": "Total income",
      "total_expense": "Total expenses",
      "balance": "Balance",
      "add": "Add transaction",
      "add_title": "New transaction",
      "add_submit": "Add",
      "edit": "Edit",
      "edit_title": "Edit transaction",
      "edit_submit": "Save",
      "delete": "Delete",
      "empty": "No transactions recorded yet.",
      "created": "Transaction added",
      "updated": "Transaction updated",
      "deleted": "Transaction deleted",
      "validation": "A label, amount and VAT rate are required.",
      "result_title": "Result & VAT",
      "result_net_ht": "Net result (excl. VAT)",
      "result_gross": "Gross profit (incl. VAT)",
      "result_net": "Net profit (excl. VAT)",
      "vat_collected": "VAT collected",
      "vat_deductible": "VAT deductible",
      "vat_net": "Net VAT (deductible − collected)",
      "result_real": "Real result",
      "crews_title": "Crews & split",
      "crews_copy": "Co-producing crews sharing this event's result.",
      "crews_empty": "No participating crew yet.",
      "crews_total_warning": "Shares total {{total}}% (should be 100%).",
      "crews_col_name": "Crew",
      "crews_col_share": "Share",
      "crew_select": "Select a crew",
      "crew_name_label": "Crew name",
      "crew_name_placeholder": "e.g. FalcOhm",
      "crew_share_label": "Share key (%)",
      "crew_add": "Add",
      "crew_added": "Crew added",
      "crew_removed": "Crew removed",
      "crew_updated": "Crew updated",
      "crew_remove": "Remove crew",
      "multi_org_label": "Multi-organisation event",
      "multi_org_copy": "Enable to split the accounting across several co-producing organisations.",
      "multi_org_disable_confirm": "Disabling will delete the {{count}} configured split(s) and hide the Organisation column. Continue?",
      "multi_org_disable_action": "Disable",
      "multi_org_cancel": "Cancel",
      "multi_org_disabled": "Multi-organisation mode disabled",
      "settlement_title": "Inter-crew settlement",
      "settlement_empty": "Add participating crews to compute the settlement.",
      "settlement_warning": "The common pot has non-cash lines not covered by the cash split — review manually.",
      "settlement_result_note": "Each crew keeps 100% of its own income/expenses; only the common loss (or profit) is shared per share.",
      "settlement_col_crew": "Crew",
      "settlement_col_income": "Own income (excl. VAT)",
      "settlement_col_expense": "Own expenses (excl. VAT)",
      "settlement_col_common_share": "Common share (excl. VAT)",
      "settlement_col_final": "Final balance (excl. VAT)",
      "settlement_debug_toggle": "Debug JSON",
      "settlement_col_tva": "Net VAT",
      "settlement_col_cash": "Cash share",
      "settlement_flows_title": "Where does this amount come from?",
      "settlement_flows_empty": "No restitution or share to split.",
      "settlement_flows_owes": "{{from}} owes {{to}}",
      "settlement_flows_subtotal": "Subtotal",
      "settlement_flows_help": "Each crew owes the others money (its share of the expenses they advanced) and is owed money by them (its share of the income they collected). We add all of that up per crew to get a single balance, then have the debtors pay the creditors. Hence a handful of wires instead of dozens.",
      "settlement_final_help": "What is left to wire once everything is offset. Each amount comes straight from the net balances just below: a crew in debt wires to a crew in credit. VAT is added on top, because this wire is invoiced between crews.",
      "settlement_balance_note": "For each crew: where it actually stands today, against what it should end up with once the result is split. The gap is what it is owed, or what it has to hand back.",
      "settlement_story_paid": "What it paid",
      "settlement_story_collected": "What it collected",
      "settlement_story_cash": "Its share of the cash pot",
      "settlement_story_cash_tag": "cash",
      "settlement_story_cash_note": "The cash share is settled hand to hand: it is not in the wire, nor on the invoice.",
      "settlement_story_position": "→ Where it stands today",
      "settlement_story_fair": "Its fair share of the result",
      "settlement_story_gets_back": "It is owed",
      "settlement_story_pays_back": "It pays in",
      "settlement_story_why_gets_back": "It carried more than its share: the difference comes back to it.",
      "settlement_story_why_pays_back": "It carried less than its share: it pays the difference in.",
      "settlement_flows_show_detail": "Show the line-by-line detail ({{count}} pairs)",
      "settlement_flows_hide_detail": "Hide the line-by-line detail",
      "settlement_split_title": "What each crew ends up carrying",
      "settlement_reason_own_income": "{{to}}'s own income collected by {{from}}",
      "settlement_reason_own_expense": "{{from}}'s own expenses paid by {{to}}",
      "settlement_reason_common_income": "{{to}}'s share of common income collected by {{from}}",
      "settlement_reason_common_expense": "{{from}}'s share of common expenses advanced by {{to}}",
      "settlement_recap_title": "Per-crew recap",
      "recap_paid_ht": "Paid (excl. VAT)",
      "recap_paid_vat": "VAT paid",
      "recap_paid_ttc": "Paid (incl. VAT)",
      "recap_collected_ht": "Collected (excl. VAT)",
      "recap_collected_vat": "VAT collected",
      "recap_collected_ttc": "Collected (incl. VAT)",
      "settlement_cash_title": "Common cash pot breakdown",
      "settlement_cash_recettes": "Cash income",
      "settlement_cash_depenses": "Cash expenses",
      "settlement_cash_solde": "Common cash balance",
      "settlement_cash_shared_note": "of which common slice split per share: {{amount}}",
      "settlement_cash_crew_split": "{{shared}} common + {{own}} own cash",
      "settlement_cash_crew_shared_only": "{{shared}} common",
      "settlement_no_transfer": "No transfer needed (everything nets out).",
      "settlement_final_title": "The transfer to make",
      "settlement_reimb_note": "A single net wire per crew: at the dominant VAT rate of the income the issuer collected, or 21% when it is covering a loss (collected nothing). Cash is settled physically, off-invoice.",
      "vat_exempt": "VAT exempt",
      "sim_title": "Break-even simulator",
      "sim_hint": "Universal rule: break-even = fixed costs ÷ entry price (excl. VAT). The bar is optional — leave it at 0 for a conservative break-even.",
      "sim_fixed_costs": "Fixed costs to cover (excl. VAT)",
      "sim_capacity": "Venue capacity",
      "sim_ticket_price": "Entry price (incl. VAT)",
      "sim_bar_spend": "Bar spend / person (optional)",
      "sim_bar_margin": "Bar margin",
      "sim_contribution": "Contribution / entry",
      "sim_breakeven": "Break-even (entries)",
      "sim_fill_needed": "Fill rate needed",
      "sim_attendance": "Expected attendance",
      "sim_revenue": "Revenue (excl. VAT)",
      "sim_result": "Result",
      "sim_suggested_price": "Suggested price at this fill",
      "sim_bar_ref": "Real bar margin from the ledger: {{amount}} — use it to set the per-person bar margin.",
      "export_pdf": "Export to PDF",
      "config_title": "Accounting settings",
      "config_copy": "Configure the co-producing crews and how they share this event's result.",
      "config_done": "Done",
      "expenses_title": "Expenses",
      "income_title": "Income",
      "grid_hint": "Type in the cells — a new line is created as you fill the bottom row.",
      "grid_search": "Search…",
      "grid_no_match": "No match.",
      "grid_detail_placeholder": "Description…",
      "grid_new_row": "new line",
      "grid_add_row": "Add a transaction",
      "grid_total": "Total",
      "col_vat_rate": "VAT %",
      "col_ttc": "Incl. VAT",
      "col_ht": "Excl. VAT",
      "col_vat_amount": "VAT",
      "col_mode": "Payment",
      "col_org": "Paid for",
      "col_org_income": "Goes to",
      "col_paid_by": "Paid by",
      "col_received_by": "Received by",
      "col_settled_by": "Paid/Received by",
      "col_resize": "Resize column",
      "from_contract": "Amount from a signed contract — edit it from the contract",
      "mode_cash": "Cash",
      "mode_card": "Card",
      "mode_transfer": "Transfer",
      "status_paid": "Paid",
      "status_to_pay": "To pay",
      "status_provisional": "Provisional",
      "type": "Type",
      "type_income": "Income",
      "type_expense": "Expense",
      "date": "Date",
      "label": "Label",
      "amount_incl_vat": "Amount (incl. VAT)",
      "vat_rate": "VAT rate",
      "vat_placeholder": "Select a rate",
      "category": "Category",
      "category_placeholder": "Optional category",
      "crew": "Crew",
      "crew_placeholder": "Optional crew",
      "common": "Common",
      "payment_mode": "Payment",
      "payment_cash": "Cash",
      "payment_card": "Card",
      "payment_transfer": "Transfer",
      "status": "Status",
      "optional": "Optional",
      "col_date": "Date",
      "col_label": "Label",
      "col_category": "Category",
      "col_crew": "Crew",
      "col_amount": "Amount",
      "col_status": "Status",
      "col_actions": "Actions",
      "grid_incomplete": "Fill in a description and an amount incl. VAT to add the line."
    },
    "tab_copy": {
      "roles": "This event currently tracks {{count}} role slots. Role management lands in the next epic, but this page already surfaces the count and readiness.",
      "volunteers": "{{count}} volunteers are attached to this event. Volunteer management will expand from this base in the next dedicated workflow.",
      "contracts": "Contract signing is reserved for EPIC-10. Current progress for this event is {{signed}}/{{total}} signed contracts.",
      "planning_ready": "A planning has already been generated for this event.",
      "planning_missing": "No planning has been generated yet for this event."
    },
    "complete": {
      "title": "Mark event as completed",
      "copy": "Mark \"{{name}}\" as completed. Volunteers will then be able to leave feedback on this event."
    },
    "feedback": {
      "title": "Volunteer feedback",
      "subtitle": "Comments left by volunteers after the event.",
      "empty": "No feedback has been left yet.",
      "anonymous": "Anonymous",
      "sort_author": "Author",
      "sort_date": "Date"
    },
    "archive": {
      "title": "Archive event",
      "copy": "Archiving {{name}} will switch the event to read-only mode."
    },
    "guests_tab": {
      "add_success": "Guest added to the event",
      "remove": "Remove",
      "mark_present": "Check in",
      "unmark_present": "Undo check-in",
      "present": "Present",
      "share": "Share",
      "share_title": "Share the guest list",
      "share_copy": "Anyone with this read-only link can view the guest list (names, number of guests and presence). Revoke it any time.",
      "share_revoke": "Revoke link",
      "share_revoked": "Share link revoked",
      "copy": "Copy a list",
      "copy_title": "Copy a guest list",
      "copy_copy": "Import the guests from another event of your organisation into this one. Already invited contacts are skipped.",
      "copy_no_source": "No other event in your organisation yet, so there is nothing to copy from.",
      "copy_source_label": "Source event",
      "copy_source_placeholder": "Choose an event…",
      "copy_submit": "Copy guests",
      "copy_success": "{{count}} guest(s) copied",
      "export_pdf": "Export PDF",
      "empty": "No guests have been invited to this event yet.",
      "count_label": "Number of guests: {{count}}",
      "list_search_placeholder": "Search a guest",
      "no_match": "No guest matches your search.",
      "col_first_name": "First name",
      "col_last_name": "Last name",
      "col_guest_count": "# Guests",
      "col_invited_by": "Invited by",
      "invited_by_placeholder": "Artist, contact…",
      "add_row": "Add a row"
    },
    "volunteers_tab": {
      "add": "Add volunteer",
      "add_title": "Add volunteers to event",
      "add_submit": "Add selected",
      "add_success": "Volunteers added to the event",
      "export_remunerations": "Export remunerations (CSV)",
      "search_label": "Search volunteers",
      "search_placeholder": "Search by name or email…",
      "search_empty": "No volunteer matches this search.",
      "available_count": "{{count}} volunteers available",
      "selected_count": "{{count}} selected",
      "email_missing": "No email provided",
      "copy_contact_aria": "Copy {{value}} to clipboard",
      "col_name": "Name",
      "col_email": "Email",
      "col_phone": "Phone",
      "col_contact": "Contact",
      "col_role": "Role",
      "col_primary_role": "Primary role",
      "col_remuneration": "Remuneration",
      "col_contract": "Contract",
      "col_actions": "Actions",
      "empty": "No volunteers are attached to this event yet.",
      "remove": "Remove",
      "save_link": "Save",
      "saved": "Saved ✓",
      "update_success": "Volunteer link updated",
      "registration_approve": "Approve",
      "registration_reject": "Reject",
      "registration_approved": "Registration approved",
      "registration_rejected": "Registration rejected",
      "role_placeholder": "Choose a role",
      "primary_role_label": "Primary role",
      "remuneration_label": "Remuneration",
      "role_unassigned": "No role",
      "role_count_one": "{{count}} role",
      "role_count_other": "{{count}} roles",
      "role_more_one": " +{{count}} more",
      "role_more_other": " +{{count}} more",
      "assign_loading": "Updating…",
      "assign_success": "Role assigned successfully",
      "status_none": "Without",
      "status_generated": "Generated",
      "status_sent": "Sent",
      "status_signed": "Signed",
      "event_response_label": "Event",
      "event_response_none": "No response",
      "shift_response_label": "Shift",
      "shift_response_none": "No shift",
      "shift_response_pending": "Pending",
      "shift_response_confirmed": "Confirmed present",
      "info_notes": "Notes",
      "info_no_notes": "No notes recorded",
      "info_origin": "Origin",
      "info_origin_self": "Registered from the volunteer app",
      "info_origin_self_on": "Registered from the volunteer app on {{date}}",
      "info_origin_by_on": "Added by {{name}} on {{date}}",
      "info_origin_admin_on": "Added by an organiser on {{date}}",
      "info_origin_untracked": "Added by an organiser (before additions were tracked)",
      "comp_title": "Pay & expenses",
      "comp_autosave": "Saved automatically",
      "comp_saving": "Saving…",
      "comp_saved": "Saved",
      "comp_days_one": "{{count}} day in the period",
      "comp_days_other": "{{count}} days in the period",
      "comp_forfait_label": "Lump sum (€)",
      "comp_forfait_max": "Up to {{max}} for the period",
      "comp_forfait_over": "Above the {{max}} maximum for the period",
      "comp_no_cap": "No lump-sum cap configured",
      "comp_km_amount": "= {{amount}} at {{rate}} €/km",
      "comp_no_rate": "No km rate configured",
      "comp_total": "Total paid",
      "comp_split_title": "Split an amount",
      "comp_target_label": "Amount to pay (€)",
      "comp_split_apply": "Apply",
      "comp_split_preview": "{{forfait}} lump sum + {{km}} km ({{kmAmount}}) = {{total}}",
      "comp_split_uncovered": "{{amount}} cannot be paid: configure the km rate.",
      "info_max_hours": "Max {{hours}}h",
      "info_rating": "Rating: {{rating}}/5",
      "km_reimbursement_label": "Km reimbursed",
      "contract_period_start_label": "Contract start",
      "contract_period_end_label": "Contract end"
    },
    "package": {
      "chip": "{{tier}} · {{volunteers}} volunteers",
      "access_until": "Full access until {{date}}",
      "badge": "Paid",
      "hint": "This event is covered by its own package."
    },
    "phases": {
      "setup": "Setup",
      "show": "Event",
      "teardown": "Teardown",
      "setup_hint": "Preparation days before the event, even outside its dates.",
      "show_hint": "One window per event day, rebuilt from the dates.",
      "teardown_hint": "Clean-up days after the event.",
      "empty_setup": "No setup day.",
      "empty_teardown": "No teardown day.",
      "add_setup_day": "Add a setup day",
      "add_teardown_day": "Add a teardown day",
      "remove_day": "Remove this day",
      "date_label": "Day",
      "max_reached": "{{max}} days max per phase",
      "shift_prompt": "The event dates changed. Shift the setup and teardown days — and their roles, assignments and availabilities — by the same amount?",
      "shift_apply": "Shift",
      "shift_dismiss": "Keep current days",
      "delete_roles_confirm_title": "Some roles will be deleted",
      "delete_roles_confirm_body_one": "{{count}} role no longer fits in the new time windows and will be deleted, along with its assignments:",
      "delete_roles_confirm_body_other": "{{count}} roles no longer fit in the new time windows and will be deleted, along with their assignments:",
      "delete_roles_confirm_button": "Save and delete",
      "delete_roles_revert": "Undo the change",
      "confirm_windows_first": "Save or undo the time window change first, then create the roles.",
      "delete_roles_assigned_one": "{{count}} volunteer assigned",
      "delete_roles_assigned_other": "{{count}} volunteers assigned",
      "deleted_roles_toast_one": "{{count}} role outside the time windows was deleted",
      "deleted_roles_toast_other": "{{count}} roles outside the time windows were deleted",
      "shifted_roles_toast_one": "{{count}} role shifted with its phase",
      "shifted_roles_toast_other": "{{count}} roles shifted with their phase",
      "error_overlap": "This window overlaps another window.",
      "error_out_of_phase": "Setup must end before the event starts, and teardown must start after it ends.",
      "error_too_many": "At most {{max}} setup days and {{max}} teardown days.",
      "error_show_out_of_range": "This day is outside the event dates.",
      "error_duplicate": "This day is already used for this phase.",
      "error_no_show": "The event must keep at least one window.",
      "roles_check_failed": "The roles could not be checked against the new time windows. Nothing was saved — try again.",
      "window_setup": "Setup window",
      "window_show": "Event window",
      "window_teardown": "Teardown window",
      "windows_unavailable": "The setup, event and teardown windows could not be loaded. Reload the page to edit them; the other fields can still be saved.",
      "windows_loading": "Loading the setup, event and teardown windows…"
    },
    "lineup": {
      "title": "Line-up",
      "subtitle": "Who plays where and when, and where each booking stands. One version for the whole team.",
      "view_label": "View",
      "view_list": "List",
      "view_timetable": "Timetable",
      "filter_label": "Status",
      "filter_all": "All statuses",
      "filter_active": "Without ideas or cancelled sets",
      "add": "Add an artist",
      "add_title": "Add a set",
      "edit_title": "Edit {{artist}}'s set",
      "edit_aria": "Edit {{artist}}'s set",
      "stages": "Stages",
      "add_stage": "Add",
      "new_stage_placeholder": "New stage",
      "remove_stage": "Remove the {{stage}} stage",
      "remove_stage_confirm": "Remove {{stage}}? Its sets will move to \"No stage\".",
      "remove_stage_yes": "Remove",
      "remove_stage_no": "Cancel",
      "no_stage": "No stage",
      "unscheduled": "To be scheduled",
      "empty": "No artist on the line-up yet.",
      "artist": "Artist",
      "artist_placeholder": "Stage name",
      "artist_suggestions": "Known artists",
      "stage": "Stage",
      "day": "Day",
      "start": "Start",
      "end": "End",
      "status_label": "Status",
      "amounts": "Amounts",
      "fee_excl_vat": "Fee excl. VAT (€)",
      "vat_rate": "VAT",
      "no_vat_rate": "No VAT",
      "agency_fee_excl_vat": "Agency fee excl. VAT (€)",
      "per_diem": "Per diem (€)",
      "fee_short": "Fee {{amount}}",
      "notes": "Notes",
      "save": "Save",
      "delete": "Remove from the line-up",
      "delete_confirm": "Confirm removal",
      "saved": "Line-up updated",
      "removed": "Set removed from the line-up",
      "overlap_warning": "Heads up: this set overlaps another one on the same stage.",
      "status": {
        "idea": "Idea",
        "option": "Option",
        "confirmed": "Confirmed",
        "contract_signed": "Contract signed",
        "announced": "Announced",
        "cancelled": "Cancelled"
      },
      "time": "Time",
      "contact": "Contact",
      "fee_column": "Fee excl. VAT"
    }
  },
  "roles": {
    "title": "Event roles",
    "subtitle": "{{count}} roles are currently configured for this event.",
    "event_window": "Event window",
    "add_button": "New role",
    "form_title": "Create a role",
    "form_copy": "Define clear time slots so planning is built from distinct posts.",
    "batch_title": "Create several slots at once",
    "batch_copy": "Give the role a shared name, then add as many time slots as needed to cover the night or the full event.",
    "form_role_type": "Role type",
    "form_role_type_placeholder": "Choose an existing type",
    "form_role_type_new": "New role type",
    "role_type_create_button": "Create a type",
    "role_type_use_existing": "Use an existing type",
    "role_type_save": "Save type",
    "role_type_create_success": "Role type “{{name}}” saved",
    "form_name": "Role name",
    "form_description": "Description (optional)",
    "form_start_date": "Start date",
    "form_end_date": "End date",
    "form_start_time": "Start time",
    "form_end_time": "End time",
    "form_hint": "The x1/x2/x3/x4 selector creates several lanes at once. Each lane then becomes its own distinct post.",
    "planner_title": "Build the role coverage",
    "planner_copy": "Paint the hours you need to cover, choose how many parallel lanes/posts are needed, then generate the shifts automatically.",
    "planner_hint": "Drag over the timeline to mark a range. The x1/x2/x3/x4 selector creates visible lanes for the real number of simultaneous posts.",
    "planner_granularity": "Step",
    "planner_granularity_hour": "1h",
    "planner_granularity_half": "30 min",
    "planner_need": "Need",
    "planner_lead": "Lead",
    "planner_lead_label": "Responsible poste",
    "planner_lead_type_placeholder": "Anyone",
    "planner_visible_window": "Visible range",
    "planner_visible_from": "Show from",
    "planner_visible_to": "Show until",
    "planner_show_day": "Show grid",
    "planner_hide_day": "Hide grid",
    "planner_phase_hint": "One phase at a time. What you draw on the others is kept — the number on each tab is what it already holds.",
    "planner_time_axis": "Timeline",
    "planner_lane": "Post {{index}}",
    "planner_summary_title": "Painted zones",
    "planner_summary_copy": "Each zone can carry its own role name and its own shift duration.",
    "planner_summary_empty": "No coverage zone painted yet.",
    "planner_summary_need": "{{count}} simultaneous post",
    "planner_summary_need_other": "{{count}} simultaneous posts",
    "planner_zone_role": "Role name for this zone",
    "planner_zone_role_placeholder": "Example: Head bar",
    "planner_zone_shift_duration": "Shift split for this zone",
    "planner_delete_zone": "Delete this zone",
    "planner_generation_title": "Split into shifts",
    "planner_generation_copy": "Set the shift duration directly inside each painted zone on the left, then generate or regenerate the shifts here.",
    "planner_shift_duration_option": "{{count}}h",
    "planner_shift_duration_full": "Whole shift",
    "planner_generate": "Generate shifts",
    "planner_generate_again": "Regenerate shifts",
    "planner_generated_title": "Generated shifts",
    "planner_generated_copy": "Review, delete, or resize each generated shift before saving the role.",
    "planner_generated_empty": "No shift generated yet.",
    "planner_generated_footer": "Final coverage summary",
    "planner_resize_start": "Start",
    "planner_resize_end": "End",
    "planner_extend_earlier": "Extend earlier",
    "planner_shorten_start": "Move start later",
    "planner_shorten_end": "Move end earlier",
    "planner_extend_later": "Extend later",
    "planner_delete_shift": "Delete this shift",
    "slots_title": "Compose the slots",
    "slots_copy": "Stack time ranges like a small planner. Each card describes one precise moment to cover.",
    "form_submit": "Add role",
    "add_slot": "Add slot",
    "duplicate_slot": "Duplicate",
    "remove_slot": "Remove this slot",
    "slot_title": "Slot {{index}}",
    "slot_copy": "Date, time, and staffing target for this block.",
    "slot_overview": "Overview",
    "slot_start": "Start",
    "slot_end": "End",
    "slot_staffing": "Staffing",
    "slot_summary_pending": "Slot to complete",
    "slot_people": "{{count}} person required",
    "slot_people_other": "{{count}} people required",
    "slot_people_pending": "Staffing to define",
    "overview_title": "Coverage overview",
    "overview_copy": "Read the coverage again on a visual timeline before dropping into the detailed line-by-line list.",
    "overview_lane_suffix": "lane {{index}}",
    "list_title": "Current coverage",
    "list_copy": "The list is grouped by role, then sorted by schedule, while highlighting roles that still need staffing.",
    "col_slot": "Row",
    "col_name": "Role",
    "col_schedule": "Schedule",
    "col_required": "Required",
    "col_assigned": "Assigned",
    "col_actions": "Actions",
    "empty": "No roles are defined for this event yet.",
    "description_empty": "No extra description",
    "coverage_complete": "Complete",
    "coverage_partial": "Partial",
    "coverage_empty": "Empty",
    "coverage_tooltip": "{{assigned}} assigned for {{required}} required",
    "schedule_same_day": "Single-day slot",
    "schedule_multi_day": "Multi-day slot",
    "validation_form": "Fill in the name, times, and a valid staffing count.",
    "validation_role_type": "Choose or create a role type first.",
    "validation_name": "Add the role name first so every slot shares a clear identity.",
    "validation_slot_inline": "Some slots still need corrections before saving.",
    "validation_slot_form": "Slot {{index}} is incomplete or invalid.",
    "validation_slot_order": "Slot {{index}} ends before it starts.",
    "validation_order": "The end of the slot must be later than the start.",
    "validation_slot_window": "Slot {{index}} must stay within the event window.",
    "validation_window": "The time slot must stay within the event window.",
    "validation_planner_empty": "Paint at least one coverage zone before saving the role.",
    "validation_generate_first": "Generate the shifts first so the final role coverage can be reviewed.",
    "create_success": "{{count}} role(s) created successfully",
    "delete_button": "Delete",
    "delete_title": "Delete role",
    "delete_copy": "Deleting {{name}} will remove this slot from the list.",
    "delete_warning": "{{count}} active assignment(s) are linked to this role. They will be deleted in cascade.",
    "delete_confirm": "Confirm deletion",
    "delete_success": "Role deleted successfully",
    "delete_group_button": "Delete grid",
    "delete_group_title": "Delete grid",
    "delete_group_copy": "Deleting the {{name}} grid will remove {{count}} role(s) from this window.",
    "delete_group_warning": "{{count}} active assignment(s) are linked to this grid. They will be deleted in cascade.",
    "delete_group_confirm": "Confirm deletion",
    "delete_group_success": "{{count}} role(s) deleted successfully",
    "delete_group_partial": "{{count}} role(s) out of {{total}} were deleted before an error occurred.",
    "delete_group_error": "Unable to delete this grid",
    "assign_title": "Assign volunteers to {{name}}",
    "assign_confirm": "Save assignment",
    "assign_success": "Assignment updated",
    "assign_search_label": "Search volunteers",
    "assign_search_placeholder": "Search by name or email…",
    "assign_search_empty": "No volunteer matches this search.",
    "assign_available_count": "{{count}} volunteers attached to the event",
    "assign_selected_count": "{{count}} / {{required}} selected",
    "assign_post_count_one": "{{count}} role total",
    "assign_post_count_other": "{{count}} roles total",
    "assign_already_selected": "Already assigned to this role",
    "assign_capacity_error": "This role is limited to {{count}} people",
    "assign_unassigned": "To assign",
    "edit_title": "Edit role",
    "edit_confirm": "Save changes",
    "edit_success": "Role updated successfully",
    "edit_warning": "{{count}} active assignment(s) are linked to this role. Changing the time window may affect them.",
    "edit_role_type_label": "Role type",
    "edit_name_label": "Role name",
    "edit_start_label": "Start",
    "edit_end_label": "End",
    "edit_description_label": "Description (optional)",
    "edit_contract_template_label": "Contract template",
    "edit_contract_template_placeholder": "No template selected",
    "duplicate_button": "Duplicate",
    "duplicate_success": "Role duplicated. Edit the time window to avoid overlap.",
    "backup_badge": "Backup",
    "responsible_badge": "Lead",
    "coverage_title": "Coverage at a glance",
    "coverage_copy": "Quick view of how many roles are staffed vs still empty for this event.",
    "coverage_total": "Total",
    "coverage_covered": "Covered",
    "coverage_empty_count": "Empty",
    "coverage_count": "{{covered}} / {{total}}",
    "coverage_breakdown": "Breakdown by role type",
    "day_windows_button": "Time windows",
    "day_windows_title": "Day time windows",
    "day_windows_copy": "Set the visible time window for each day. These windows determine what is shown in the coverage timeline.",
    "day_windows_col_start": "Start",
    "day_windows_col_end": "End",
    "day_windows_save": "Save",
    "day_windows_saved": "Time windows saved",
    "planner_granularity_minutes": "{{count}} min",
    "planner_granularity_forced": "Set by the time window edges",
    "unassign_volunteer": "Remove {{name}} from this role"
  },
  "volunteers": {
    "eyebrow": "Team",
    "list_title": "Volunteers",
    "list_subtitle": "Manage all volunteers of {{name}} from a single surface.",
    "new_title": "Add a volunteer",
    "new_subtitle": "Fill in the details to add a volunteer to your organisation.",
    "add_volunteer": "Add volunteer",
    "actions_eyebrow": "Actions",
    "actions_title": "Team",
    "stat_total": "Volunteers",
    "stat_active": "Active on events",
    "stat_rated": "Rated volunteers",
    "stat_average_rating": "Average rating",
    "breakdown_lead": "Leads",
    "breakdown_member": "Volunteers",
    "search_label": "Search volunteers",
    "search_placeholder": "Search by name, nickname or email…",
    "sort_by": "Sort:",
    "sort_name": "Name",
    "sort_rating": "Rating",
    "col_name": "Name",
    "col_email": "Email",
    "col_phone": "Phone",
    "col_rating": "Rating",
    "loading": "Loading…",
    "page_info": "Page {{page}} / {{total}}",
    "first_name_label": "First name",
    "first_name_placeholder": "Jane",
    "last_name_label": "Last name",
    "last_name_placeholder": "Doe",
    "email_label": "Email",
    "phone_label": "Phone",
    "phone_placeholder": "+32 …",
    "notes_label": "Notes (optional)",
    "max_hours_label": "Max hours (optional)",
    "max_hours_placeholder": "e.g. 6",
    "tags_label": "Tags (optional)",
    "tags_placeholder": "bar, security, logistics",
    "responsible_label": "Lead (responsable)",
    "responsible_hint": "A lead can be assigned to lead postes, and is prioritised for them.",
    "rating_label": "Rating",
    "rating_clear": "Clear",
    "show_nickname_label": "Show nickname on paper schedules",
    "show_nickname_hint": "When enabled, the nickname replaces the name on planning PDFs.",
    "show_nickname_no_nickname_hint": "This volunteer has no nickname.",
    "nickname_label": "Nickname (optional)",
    "nickname_placeholder": "e.g. Johnny",
    "nickname_too_short": "Nickname must be at least 4 characters",
    "personal_info_admin": "Administrative details",
    "national_number_label": "National number",
    "national_number_placeholder": "00.00.00-000.00",
    "bank_account_label": "Bank account (IBAN)",
    "bank_account_placeholder": "BE00 0000 0000 0000",
    "personal_info_address": "Address",
    "address_street_label": "Street",
    "address_street_placeholder": "Rue de la Loi",
    "address_number_label": "No.",
    "address_zip_label": "Postal code",
    "address_city_label": "City",
    "address_country_label": "Country",
    "address_country_placeholder": "BE",
    "notes_placeholder": "Availability, skills, remarks…",
    "first_name_required": "First name is required",
    "last_name_required": "Last name is required",
    "email_required": "Email is required",
    "email_invalid": "Email is invalid",
    "phone_required": "Phone is required",
    "phone_invalid": "Phone number is invalid",
    "no_organisation": "No active organisation selected",
    "cancel": "Cancel",
    "save": "Save",
    "saving": "Saving…",
    "create_success": "Volunteer added successfully",
    "edit": "Edit",
    "edit_title": "Edit volunteer",
    "update_success": "Volunteer updated successfully",
    "delete": "Delete",
    "delete_title": "Delete volunteer",
    "delete_copy": "Delete {{name}} from your volunteer base.",
    "delete_data_hint": "Their personal data (contact details, national number, bank account, address, photo) is erased. Contracts already generated or signed and their accounting entries are kept, without their name.",
    "delete_confirm": "Confirm deletion",
    "delete_success": "Volunteer deleted successfully",
    "delete_assignments_warning": "{{count}} active assignment(s) will also disappear with this volunteer.",
    "delete_no_assignments": "No active assignment is linked to this volunteer.",
    "import_button": "Import CSV",
    "import_title": "Import volunteers",
    "import_copy": "Upload a CSV file with first_name, last_name, email, and phone columns.",
    "import_file_label": "CSV file",
    "import_preview_title": "Preview (first 10 rows)",
    "import_preview_empty": "Select a CSV file to preview the import.",
    "import_preview_error": "The CSV preview could not be generated. Check the expected columns.",
    "import_submit": "Import volunteers",
    "import_success": "{{imported}} imported, {{skipped}} skipped",
    "import_report_imported": "{{count}} imported",
    "import_report_skipped": "{{count}} skipped",
    "import_line": "Line",
    "col_actions": "Actions"
  },
  "planning": {
    "title": "Planning",
    "subtitle": "Automatically generate optimal assignments for this event.",
    "generate_button": "Generate planning",
    "regenerate_button": "Regenerate",
    "regenerate_title": "Regenerate planning",
    "regenerate_copy": "Are you sure you want to re-run planning generation?",
    "regenerate_warning": "Auto-generated assignments will be replaced. Manual assignments will be kept.",
    "regenerate_confirm": "Confirm regeneration",
    "generating": "Calculating… (may take up to 30 seconds)",
    "empty": "No planning generated yet. Click \"Generate planning\" to get started.",
    "preconditions_warning": "Preconditions are not met:",
    "precondition_no_roles": "No roles defined for this event",
    "precondition_no_volunteers": "No volunteers added to this event",
    "stat_score": "Score",
    "stat_total_roles": "Total roles",
    "stat_covered_roles": "Covered roles",
    "stat_assigned": "Assigned volunteers",
    "stat_unassigned": "Unassigned",
    "score_unknown": "–",
    "unassigned_title": "Unassigned volunteers",
    "unassigned_copy": "These volunteers could not be placed on a role. Here's why.",
    "unassigned_show_more": "Show more ({{count}})",
    "unassigned_show_less": "Show less",
    "conflicts_title": "Detected conflicts",
    "volunteer_id_label": "Volunteer",
    "config_title": "Algorithm configuration",
    "config_copy": "Parameters used during the next generation.",
    "config_max_hours": "Max hours per volunteer",
    "config_max_hours_help": "Maximum hours assigned during the main event phase, excluding setup and teardown days. An individual limit still takes priority.",
    "config_min_break": "Minimum break (minutes)",
    "config_min_break_help": "Minimum rest enforced between two shifts for the same volunteer before they can be reassigned.",
    "config_max_continuous": "Max continuous time (minutes)",
    "config_max_continuous_help": "Maximum time a volunteer may work back-to-back without a break (shifts closer than the minimum break count as one block). A single longer shift is never split. 0 = no limit.",
    "config_prioritize_rating": "Prioritize by rating",
    "config_save": "Save configuration",
    "coverage_title": "Coverage overview",
    "coverage_copy": "Real-time view of role assignments generated by the planning.",
    "vacated_shifts": {
      "title": "Freed shifts",
      "copy": "Shifts a volunteer freed by declining them or leaving the event. Reassign them to keep your planning complete.",
      "empty": "No freed shifts at the moment.",
      "reason_decline": "Volunteer declined",
      "reason_unregister": "Left the event",
      "reason_unknown": "Freed",
      "reason_note": "Reason: {{reason}}"
    },
    "regenerate_keep_button": "Keep & regenerate",
    "regenerate_keep_title": "Regenerate (keep existing)",
    "regenerate_keep_copy": "Uncovered roles will be filled while keeping existing assignments intact.",
    "regenerate_keep_confirm": "Confirm",
    "delete_button": "Delete planning",
    "delete_title": "Delete planning",
    "delete_copy": "Are you sure you want to delete the generated planning for this event?",
    "delete_warning": "All assignments, including manual ones, attendance confirmations, and planning results will be removed.",
    "delete_confirm": "Confirm deletion",
    "delete_success": "Planning deleted",
    "publish": {
      "published_title": "Schedule published",
      "published_copy": "Visible to volunteers since {{date}}.",
      "draft_title": "Schedule in draft",
      "draft_copy": "Hidden from volunteers until it is published.",
      "publish_button": "Publish schedule",
      "unpublish_button": "Unpublish",
      "publish_success": "Schedule published — volunteers can now see it",
      "unpublish_success": "Schedule unpublished — it is no longer visible to volunteers"
    },
    "send_schedule": {
      "send_button": "Send schedule",
      "resend_button": "Resend to all",
      "success": "Schedule sent to {{sent}} volunteer(s), {{skipped}} skipped."
    },
    "edit_volunteer_title": "Edit — {{name}}",
    "edit_availability_section": "Availabilities",
    "edit_preferences_section": "Role preferences",
    "reasons": {
      "no_availability": {
        "label": "No availability",
        "detail": "This volunteer hasn't submitted any availability for this event."
      },
      "no_matching_slot": {
        "label": "No matching slot",
        "detail": "The volunteer's availability never fully covers a role time slot."
      },
      "type_mismatch": {
        "label": "Type mismatch",
        "detail": "The volunteer's type doesn't match any role they are available for."
      },
      "max_hours_reached": {
        "label": "Hours limit reached",
        "detail": "The hours budget is too small to fit any role this volunteer could take."
      },
      "break_conflict": {
        "label": "Break conflict",
        "detail": "Overlaps or the minimum break between shifts block every remaining role."
      },
      "all_roles_filled": {
        "label": "All roles filled",
        "detail": "Every role this volunteer could take was already covered by other volunteers."
      },
      "invalid_assignment": {
        "label": "Invalid assignment",
        "detail": "An existing assignment was rejected because it no longer meets the role requirements."
      },
      "unknown": {
        "label": "Unknown reason",
        "detail": "The planner did not provide a recognised reason."
      }
    },
    "export_pdf": "Export to PDF",
    "table": {
      "title": "Planning table",
      "copy": "Who does what and when, by post and time slot.",
      "empty": "No roles defined for this event.",
      "uncovered": "Unassigned",
      "col_role": "Post",
      "col_slot": "Time slot",
      "col_volunteers": "Volunteer(s)",
      "col_status": "Status",
      "status_covered": "Covered",
      "status_empty": "To fill"
    },
    "volunteer_hours": {
      "title": "Hours per volunteer",
      "copy": "Total hours and shifts assigned to each volunteer.",
      "empty": "No volunteer is assigned yet.",
      "col_volunteer": "Volunteer",
      "col_shifts": "Shifts",
      "col_hours": "Hours"
    }
  },
  "availabilities": {
    "title": "Availabilities",
    "col_header": "Settings",
    "show": "Availabilities",
    "hide": "Hide",
    "empty": "No availability slot defined",
    "add": "Add slot",
    "delete": "Remove",
    "date": "Date",
    "start_time": "Start time",
    "end_time": "End time",
    "full_event": "Full event",
    "full_day": "Available all day",
    "clear_all": "Clear all",
    "save": "Save availability",
    "timeline_title": "Availability overview",
    "timeline_empty": "No availabilities recorded yet",
    "timeline_expand": "Show all",
    "timeline_collapse": "Collapse",
    "coverage": "Coverage",
    "save_success": "Availability saved",
    "coverage_complete": "Full coverage",
    "timeline_previous_section": "Previous time window",
    "timeline_next_section": "Next time window",
    "touch_hint": "Swipe to scroll. To paint, rest your finger for a split second, then drag."
  },
  "contracts": {
    "templates": {
      "eyebrow": "Configuration",
      "title": "Contract templates",
      "subtitle": "Write your templates inside the app. The backend then converts them to PDF and syncs them to Documenso for signing.",
      "restore_starter": "Restore starter draft",
      "editor_hint": "The content is stored in your backend and then transformed into a PDF for Documenso. Keep a signature block at the bottom of the document and use {{placeholder}} where the organisation signature should appear.",
      "editor_placeholder": "Write the contract content here...",
      "form_title": "New template",
      "edit_title": "Edit template",
      "new_template": "New template",
      "name_label": "Template name",
      "name_placeholder": "e.g. Standard volunteer contract",
      "pdf_label": "Contract PDF",
      "pdf_hint": "The PDF will be uploaded to Documenso as a signing template.",
      "pdf_placeholder": "Click to select a PDF file",
      "edit_pdf_hint": "To replace the PDF, create a new template.",
      "submit": "Create template",
      "save_changes": "Save changes",
      "reset": "Cancel",
      "list_title": "Existing templates",
      "has_documenso_template": "Linked to Documenso",
      "no_documenso_template": "No signing template",
      "empty": "No template created yet.",
      "col_name": "Name",
      "col_status": "Status",
      "status_active": "Active",
      "status_inactive": "Inactive",
      "create_success": "Template created successfully",
      "update_success": "Template updated successfully",
      "validation_required": "Template name is required",
      "validation_content_required": "Contract content is required",
      "validation_pdf_only": "Only PDF files are accepted",
      "validation_pdf_required": "A PDF file is required to create a template",
      "variables_title": "Available placeholders",
      "variables_copy": "Click a variable to insert it into the contract. It will be replaced with real data when the contract is generated / signed.",
      "languages_fr_en": "French and English",
      "languages_fr_only": "French only",
      "no_english_version": "No English version: English-speaking volunteers get the French one.",
      "start_from_default_english": "Start from the English starter"
    },
    "dashboard": {
      "stat_total": "Volunteers",
      "stat_generated": "Generated",
      "stat_sent": "Sent",
      "stat_signed": "Signed",
      "col_name": "Name",
      "col_email": "Email",
      "col_role": "Role",
      "col_remuneration": "Remuneration",
      "col_status": "Status",
      "col_sent_at": "Sent on",
      "col_signed_at": "Signed on",
      "col_reminders": "Reminders",
      "auto_reminders_label": "Automatic reminders",
      "auto_reminders_on": "Automatic reminders enabled",
      "auto_reminders_off": "Automatic reminders disabled",
      "col_signing_link": "Signing link",
      "col_actions": "Actions",
      "status_none": "Without",
      "status_generated": "Generated",
      "status_sent": "Sent",
      "status_signed": "Signed",
      "filter_label": "Filter by status",
      "filter_all": "All statuses",
      "generate_btn": "Generate",
      "generate_all_btn": "Generate all",
      "send_btn": "Send",
      "send_all_btn": "Send all",
      "remind_btn": "Remind non-signatories",
      "download_btn": "Download",
      "copy_sign_link_btn": "Copy sign link",
      "link_copied": "Link copied to clipboard",
      "sign_link_pending": "Link pending",
      "sign_link_pending_tooltip": "The Documenso submission exists but the signing link has not been resolved yet.",
      "resend_btn": "Resend",
      "regenerate_btn": "Regenerate PDF",
      "no_email_tooltip": "This volunteer has no email address",
      "empty": "No volunteers attached to this event.",
      "search_placeholder": "Search by name or email…",
      "search_empty": "No contract matches this search.",
      "generate_success": "Contract generated successfully",
      "generate_all_success_one": "{{count}} contract generated, {{skipped}}",
      "generate_all_success_other": "{{count}} contracts generated, {{skipped}}",
      "bulk_skipped_one": "{{count}} skipped",
      "bulk_skipped_other": "{{count}} skipped",
      "generate_all_progress": "Generating {{done}} / {{total}}…",
      "generate_all_empty": "No contracts to generate",
      "generate_all_error": "Bulk contract generation failed",
      "send_success": "Contract sent successfully",
      "send_all_success_one": "{{count}} contract sent, {{skipped}}",
      "send_all_success_other": "{{count}} contracts sent, {{skipped}}",
      "remind_title": "Remind non-signatories",
      "remind_copy": "{{count}} volunteer(s) will receive a reminder email. Proceed?",
      "remind_confirm": "Send reminders",
      "remind_success": "{{count}} reminder(s) sent",
      "no_template_tooltip": "No contract template assigned — click to choose one",
      "regenerate_signed_title": "Contract already signed",
      "regenerate_signed_copy": "{{name}}'s contract has already been signed. Regenerating will replace the signed version. Are you sure?",
      "regenerate_signed_confirm": "Regenerate anyway"
    },
    "list": {
      "eyebrow": "Tracking",
      "title": "Contracts — {{name}}",
      "subtitle": "Track every volunteer contract, event-linked or standalone, and see at a glance what is signed and what is still pending.",
      "stat_total": "Total",
      "stat_generated": "Generated",
      "stat_sent": "Sent",
      "stat_signed": "Signed",
      "filters_eyebrow": "Filters",
      "filters_title": "Find a contract",
      "filter_status_label": "Status",
      "filter_status_all": "All statuses",
      "filter_event_label": "Event",
      "filter_event_all": "All events",
      "search_label": "Search a volunteer",
      "search_placeholder": "Name or email",
      "new_contract": "New contract",
      "col_volunteer": "Volunteer",
      "col_attachment": "Linked to",
      "col_status": "Status",
      "col_period": "Period",
      "col_sent_at": "Sent on",
      "col_signed_at": "Signed on",
      "col_actions": "Actions",
      "col_signing_link": "Signing link",
      "copy_link_btn": "Copy link",
      "link_pending": "Link pending",
      "link_pending_tooltip": "The signing submission exists but the link is not resolved yet.",
      "link_copied": "Link copied to clipboard",
      "standalone": "Standalone",
      "col_payment": "Payment",
      "payment_paid": "Paid",
      "payment_to_pay": "To pay",
      "payment_label": "Payment of {{name}}",
      "payment_success": "Payment updated",
      "status_none": "Not generated",
      "status_generated": "Generated",
      "status_sent": "Sent",
      "status_signed": "Signed",
      "empty": "No contract matches these filters.",
      "generate_btn": "Generate",
      "send_btn": "Send",
      "resend_btn": "Resend",
      "regenerate_btn": "Regenerate",
      "download_btn": "Download signed PDF",
      "delete_btn": "Delete",
      "no_email": "This volunteer has no email address",
      "no_template": "No contract template configured. Set an organisation default template first.",
      "generate_success": "Contract generated successfully",
      "send_success": "Contract sent successfully",
      "delete_success": "Contract deleted",
      "delete_title": "Delete this contract",
      "delete_copy": "{{name}}'s standalone contract will be permanently deleted. Continue?",
      "delete_confirm": "Delete",
      "regenerate_signed_title": "Contract already signed",
      "regenerate_signed_copy": "{{name}}'s contract has already been signed. Regenerating replaces the signed version. Continue?",
      "regenerate_signed_confirm": "Regenerate anyway"
    },
    "create": {
      "title": "New standalone contract",
      "copy": "Create a contract for a volunteer without linking it to an event.",
      "volunteer_label": "Volunteer",
      "volunteer_placeholder": "Select a volunteer",
      "template_label": "Contract template",
      "template_placeholder": "Organisation default template",
      "mission_label": "Mission",
      "mission_placeholder": "e.g. Bar service — private party",
      "remuneration_label": "Remuneration (€)",
      "period_start_label": "Start date",
      "period_end_label": "End date",
      "periods_label": "Periods",
      "add_period": "Add a period",
      "remove_period": "Remove this period",
      "submit": "Create contract",
      "success": "Contract created successfully",
      "validation_volunteer": "Please select a volunteer",
      "validation_mission": "Mission is required",
      "km_label": "Km to reimburse"
    }
  },
  "preferences": {
    "title": "Role preferences",
    "subtitle": "Click a role type to mark it as wanted or to avoid.",
    "want": "Want",
    "avoid": "Avoid",
    "neutral": "Neutral",
    "no_role_types": "No role types defined for this event."
  },
  "cookies": {
    "aria_label": "Cookie consent",
    "title": "We value your privacy",
    "description": "We use cookies to keep you signed in, measure usage, and improve Coval Flow. You decide what you allow.",
    "customize": "Customize",
    "accept_all": "Accept all",
    "reject_all": "Reject all",
    "save": "Save my choices",
    "category_necessary": "Strictly necessary",
    "category_necessary_desc": "Required for sign-in, security, and core features.",
    "category_necessary_badge": "Always on",
    "category_analytics": "Analytics",
    "category_analytics_desc": "Help us understand how the workspace is used so we can improve it.",
    "category_marketing": "Marketing",
    "category_marketing_desc": "Used to personalize content and measure outreach campaigns.",
    "policy_link": "Learn more — Cookie Policy",
    "reopen_aria": "Cookie preferences"
  },
  "cookiePolicy": {
    "back": "Back to home",
    "title": "Cookie Policy",
    "updated": "Last updated: May 2026",
    "intro": "This policy explains how Coval Flow uses cookies and similar technologies, and how you can control them. You can accept, refuse, or fine-tune the use of non-essential cookies at any time.",
    "what_title": "What is a cookie?",
    "what_body": "A cookie is a small file stored on your device when you visit a website. Similar technologies such as localStorage work the same way. They let the application run correctly, remember your choices, and — only with your consent — measure usage.",
    "categories_title": "Categories of cookies we use",
    "necessary_title": "Strictly necessary cookies",
    "necessary_body": "Required to sign you in, keep your session secure, and run core features. They cannot be disabled because the service would not work without them, and they are exempt from consent.",
    "analytics_title": "Analytics cookies",
    "analytics_body": "Help us understand how the workspace is used so we can improve it. They are set only after you give consent and can be refused without affecting your use of the application.",
    "marketing_title": "Marketing cookies",
    "marketing_body": "Used to personalize content and measure outreach campaigns. They are set only after you give consent and are never required to use the service.",
    "manage_title": "Managing your preferences",
    "manage_body": "You can change or withdraw your consent at any time. Reopen the cookie panel below, or use the cookie button shown at the bottom-left of every page.",
    "manage_button": "Manage cookie preferences",
    "retention_title": "How long is your choice kept?",
    "retention_body": "Your consent choice is stored on your device for up to 6 months. After that delay, or if this policy changes, the cookie banner is shown again so you can decide afresh. A timestamped record of each decision is also kept as evidence of consent.",
    "rights_title": "Your rights",
    "rights_body": "Under the GDPR you can access, rectify, or erase your personal data, and withdraw consent as easily as you gave it. Refusing non-essential cookies has no negative consequence on your use of the service.",
    "contact_title": "Contact",
    "contact_body": "For any question about this policy or to exercise your rights, contact the organisation operating this workspace. Replace this section with your data controller and DPO contact details before production use."
  },
  "termsOfUse": {
    "back": "Back to home",
    "title": "Terms of Use",
    "updated": "Last updated: May 2026",
    "intro": "These Terms of Use govern your access to and use of Coval Flow. By creating an account or using the workspace, you agree to these terms. Please read them carefully.",
    "scope_title": "Scope of the service",
    "scope_body": "Coval Flow is an online workspace that helps event organisers recruit volunteers, build shift planning, and generate and sign contracts. The service is provided as-is and may evolve over time as new features are added.",
    "eligibility_title": "Eligibility and accounts",
    "eligibility_body": "You must be at least 18 years old and able to enter into a binding agreement to create an organiser account. You are responsible for the accuracy of your account details and for keeping your credentials confidential. You are accountable for all activity carried out under your account.",
    "acceptable_title": "Acceptable use",
    "acceptable_body": "You agree not to misuse the service, attempt to disrupt it, access it through unauthorised means, or use it for any unlawful purpose. You must not upload content that infringes the rights of others or that you are not entitled to share.",
    "volunteer_data_title": "Volunteer data and responsibilities",
    "volunteer_data_body": "When you add volunteers to your organisation, you act as the controller of their personal data. You are responsible for having a lawful basis to process that data, for informing volunteers, and for honouring their rights. Coval Flow processes this data only to provide the service.",
    "contracts_title": "Contracts and electronic signatures",
    "contracts_body": "The contract generation and e-signature features are tools to help you formalise agreements with your volunteers. You remain solely responsible for the content, legality, and enforceability of the documents you create and send. Coval Flow is not a party to those agreements.",
    "availability_title": "Availability and changes to the service",
    "availability_body": "We aim to keep the service available and reliable, but we cannot guarantee uninterrupted access. We may update, suspend, or discontinue features, and we will give reasonable notice of significant changes whenever possible.",
    "changes_title": "Changes to these terms",
    "changes_body": "We may update these Terms of Use to reflect changes to the service or for legal reasons. When we make material changes, we will notify you and update the date shown above. Continuing to use the service after a change means you accept the revised terms.",
    "contact_title": "Contact",
    "contact_body": "For any question about these Terms of Use, contact the organisation operating this workspace. Replace this section with your legal entity and contact details before production use."
  },
  "tour": {
    "start_button": "Help / Take the tour",
    "step_profile_title": "Welcome to your volunteer space!",
    "step_profile_desc": "This is your personal dashboard. You'll find your profile photo, contact details, and a summary of your activity here.",
    "step_stats_title": "Your activity at a glance",
    "step_stats_desc": "These four counters show your upcoming shifts, event registrations, available documents, and past shifts — updated in real time.",
    "step_nav_title": "Navigate between sections",
    "step_nav_desc": "Use these tabs to switch between your different spaces: Preferences, Upcoming, Events, Documents, and Past.",
    "step_upcoming_title": "Your upcoming shifts",
    "step_upcoming_desc": "All your assigned shifts appear here. You can confirm your attendance or report unavailability directly from this list.",
    "step_events_title": "Available events",
    "step_events_desc": "Register for events you want to join, then fill in your availability so the organiser can assign you to the right shifts.",
    "step_documents_title": "Your documents",
    "step_documents_desc": "All documents shared by the organisation are gathered here. Download your files or sign a document directly from this page.",
    "step_preferences_title": "Your preferences",
    "step_preferences_desc": "Set the maximum number of hours you can work and choose the roles you prefer or want to avoid. This helps the organiser build the best team.",
    "step_end_title": "You're all set!",
    "step_end_desc": "You now know how to navigate your volunteer space. You can restart this tour at any time by clicking the '?' button in the top card.",
    "next": "Next",
    "prev": "Back",
    "done": "Got it!"
  },
  "landing": {
    "seo_title": "Coval Flow — Plan events, coordinate volunteers, sign contracts",
    "seo_description": "Coval Flow is the all-in-one workspace for event organisers: recruit volunteers, build the planning automatically and get contracts signed online.",
    "nav": {
      "features": "Features",
      "how": "How it works",
      "pricing": "Pricing",
      "faq": "FAQ",
      "login": "Log in",
      "signup": "Sign up",
      "workspace": "My workspace",
      "volunteer_access": "Volunteer",
      "menu_aria": "Open menu"
    },
    "hero": {
      "eyebrow": "Event management platform",
      "title": "Run your events and volunteers",
      "title_highlight": "without the spreadsheets.",
      "subtitle": "Coval Flow brings volunteer recruitment, automatic shift planning and contract signing into one workspace built for event organisers.",
      "cta_primary": "Start for free",
      "cta_secondary": "See pricing",
      "note": "Free for your first event. No credit card required.",
      "stats": [
        {
          "value": "1 click",
          "label": "to a full planning"
        },
        {
          "value": "100%",
          "label": "paperless contracts"
        },
        {
          "value": "EN / FR",
          "label": "bilingual workspace"
        }
      ]
    },
    "features": {
      "eyebrow": "Everything in one place",
      "title": "One workspace for the whole event",
      "subtitle": "Stop juggling spreadsheets, group chats and paper contracts. Coval Flow connects every moving part of your event.",
      "items": [
        {
          "title": "Events",
          "desc": "Centralise every edition — dates, location, cover image and status — on a single operations board."
        },
        {
          "title": "Volunteers",
          "desc": "Build one clean volunteer base with CSV import, types, ratings, notes and availability."
        },
        {
          "title": "Roles & coverage",
          "desc": "Paint the hours you need on a visual timeline and turn them into staffed shifts."
        },
        {
          "title": "Automatic planning",
          "desc": "An algorithm assigns the right volunteer to every post, respecting availability and breaks."
        },
        {
          "title": "Contracts & e-signature",
          "desc": "Generate contracts from your templates, send them and collect signatures online."
        },
        {
          "title": "Volunteer portal",
          "desc": "Every volunteer gets their own space to follow shifts, confirm attendance and sign documents."
        }
      ]
    },
    "how": {
      "eyebrow": "How it works",
      "title": "From idea to staffed event in four steps",
      "subtitle": "Coval Flow keeps the whole workflow in one tool — no more handovers between spreadsheets and inboxes.",
      "steps": [
        {
          "title": "Create your event",
          "desc": "Set the dates, the location and import your volunteer base in minutes."
        },
        {
          "title": "Define the roles",
          "desc": "Draw the shifts and posts you need to cover across each day."
        },
        {
          "title": "Generate the planning",
          "desc": "Let the algorithm build an optimal assignment, then fine-tune it."
        },
        {
          "title": "Send the contracts",
          "desc": "Generate, send and track every signature without leaving the app."
        }
      ]
    },
    "showcase": {
      "items": [
        {
          "eyebrow": "Operations board",
          "title": "Pilot every event from one dashboard",
          "desc": "See recruitment, role coverage and contract progress for all your events at a glance — and open any of them in one click.",
          "bullets": [
            "Live counters for volunteers, coverage and contracts",
            "Draft, active, completed and archived statuses",
            "One readable board, even when the season fills up"
          ]
        },
        {
          "eyebrow": "Automatic planning",
          "title": "An optimal planning, generated in one click",
          "desc": "The planner matches volunteers to posts based on availability, role types, hour limits and breaks — and explains every unassigned volunteer.",
          "bullets": [
            "Coverage timeline with simultaneous posts",
            "Availability and role preferences taken into account",
            "Clear reasons whenever someone can't be placed"
          ]
        },
        {
          "eyebrow": "Contracts & signature",
          "title": "Signed contracts, without chasing anyone",
          "desc": "Generate contracts from your own templates, send them by email and follow each signature. Automatic reminders do the chasing for you.",
          "bullets": [
            "Reusable contract templates with placeholders",
            "Online signing and signed PDF download",
            "Automatic reminders for non-signatories"
          ]
        }
      ]
    },
    "pricing_preview": {
      "eyebrow": "Pricing",
      "title": "Pay per event, or per year",
      "subtitle": "Run your first event for free. After that, pay only for the event you're organising, or subscribe if you run several.",
      "event_note": "Only one event a year? Event packages start at €{{price}}, paid once, with no subscription.",
      "cta": "Compare all features"
    },
    "faq": {
      "eyebrow": "FAQ",
      "title": "Frequently asked questions",
      "items": [
        {
          "q": "Is Coval Flow really free to start?",
          "a": "Yes. The Free plan lets you run your first event with up to 10 volunteers, with no credit card and no time limit."
        },
        {
          "q": "What happens when I have more events?",
          "a": "It's your call: pay for the next event only, from €49, or subscribe for unlimited events all year. From three events a year, the subscription costs less."
        },
        {
          "q": "How does contract signing work?",
          "a": "You write your contract templates once, Coval Flow turns them into PDFs and collects signatures online. Signed documents are stored and downloadable."
        },
        {
          "q": "Do volunteers need an account?",
          "a": "Volunteers get their own portal to see shifts, confirm attendance and sign documents. You share a single signup link with your team."
        },
        {
          "q": "Is the platform available in French?",
          "a": "Yes. The whole workspace — and the volunteer portal — is available in English and French."
        },
        {
          "q": "Where is my data hosted?",
          "a": "Your data stays in one secure workspace per organisation. Only members you invite can access it."
        }
      ]
    },
    "cta": {
      "title": "Ready to organise your next event?",
      "subtitle": "Create your workspace in minutes and run your first event for free.",
      "primary": "Start for free",
      "secondary": "See pricing",
      "note": "No credit card required."
    },
    "footer": {
      "tagline": "The workspace for event organisers — volunteers, planning and contracts in one place.",
      "product": "Product",
      "account": "Account",
      "resources": "Resources",
      "links": {
        "features": "Features",
        "how": "How it works",
        "pricing": "Pricing",
        "faq": "FAQ",
        "signup": "Create an account",
        "login": "Log in",
        "volunteer_portal": "Volunteer portal",
        "contact": "Contact us",
        "cookies": "Cookie policy",
        "terms": "Terms of use"
      },
      "rights": "© {{year}} Coval Flow. All rights reserved."
    },
    "preview": {
      "dashboard_title": "Events overview",
      "stat_events": "Events",
      "stat_volunteers": "Volunteers",
      "stat_coverage": "Coverage",
      "stat_contracts": "Contracts",
      "status_active": "Active",
      "status_draft": "Draft",
      "planning_title": "Planning",
      "planning_score": "Score",
      "contracts_title": "Contracts",
      "status_signed": "Signed",
      "status_sent": "Sent",
      "status_generated": "Generated",
      "role_bar": "Bar",
      "role_welcome": "Welcome",
      "role_security": "Security",
      "role_stage": "Stage"
    }
  },
  "upgrade": {
    "badge": "Pro",
    "badge_hint": "This feature is part of the Pro plan",
    "title": "You've reached the limit of your plan",
    "reason": {
      "active_events": "Active events: {{current}} of {{limit}}. Your current plan does not go further.",
      "volunteers_per_event": "This event has {{current}} volunteers, your plan covers {{limit}}.",
      "team_members": "Team members: {{current}} of {{limit}}.",
      "contract_signatures": "Contract signatures: {{current}} of {{limit}}."
    },
    "feature": {
      "export_planning": "Exporting the planning as a PDF is part of the Pro plan.",
      "customize_contract_templates": "Custom contract templates are part of the Pro plan. The default template stays available.",
      "import_volunteers": "Importing volunteers from a CSV is part of the Pro plan. You can still add them one by one.",
      "auto_remind_signatures": "Automatic signing reminders are part of the Pro plan.",
      "restrict_event_access": "Restricting a member to specific events is part of the Pro plan."
    },
    "suggested_tier": "{{tier}} covers up to {{volunteers}} volunteers for €{{price}}, paid once.",
    "contact_us": "Beyond our packages, get in touch — we'll build the offer with you.",
    "hint": "Nothing is lost, all your work is saved. Pick a plan and carry on where you left off.",
    "see_plans": "See the plans",
    "later": "Later",
    "unlock_now": "Unlock for €{{price}}"
  },
  "pricing": {
    "seo_title": "Pricing — Coval Flow",
    "seo_description": "Pay per event or subscribe. Run your first event for free, with up to 10 volunteers. All prices include VAT.",
    "eyebrow": "Pricing",
    "title": "Pay per event, or per year",
    "subtitle": "One highlight a year? Pay for that event only. Several dates a season? A subscription costs less from {{events}} events a year.",
    "mode": {
      "aria": "Pricing mode",
      "event": "Per event",
      "subscription": "Subscription"
    },
    "billing": {
      "monthly": "Monthly",
      "yearly": "Yearly",
      "save_badge": "Save {{percent}}%",
      "aria": "Billing period"
    },
    "vat_note": "All prices include VAT.",
    "per_month": "/ month",
    "billed_monthly": "billed monthly",
    "billed_yearly": "billed yearly",
    "free_price": "€0",
    "free_forever": "Free forever",
    "most_popular": "Most popular",
    "most_common": "Most common",
    "event_tiers": {
      "up_to": "Up to {{volunteers}} volunteers",
      "one_off": "one-off payment",
      "cta": "Choose",
      "tiers": {
        "discovery": {
          "name": "Discovery",
          "tagline": "To get started with Coval Flow on a small event.",
          "cta": "Get started"
        },
        "s": {
          "name": "Event S",
          "tagline": "Local association, single-day event."
        },
        "m": {
          "name": "Event M",
          "tagline": "The most common format: mid-size festival or race."
        },
        "l": {
          "name": "Event L",
          "tagline": "Large festival, several days and several areas."
        },
        "xl": {
          "name": "Event XL",
          "tagline": "Very large volunteer operation, up to a thousand people."
        }
      },
      "included_title": "Included with every event package",
      "included": [
        "Volunteers, roles and availability",
        "Coverage timeline and shifts",
        "Automatic planning and manual adjustments",
        "Contract generation and e-signature",
        "Custom contract templates",
        "CSV volunteer import",
        "Automatic signing reminders",
        "Volunteer portal in English and French",
        "PDF and CSV exports"
      ],
      "access_note": "Full access for {{months}} months around the event date, then a read-only archive — your PDF and CSV exports stay available.",
      "overage_note": "More volunteers than planned? Your event keeps running: we simply offer you the next size up, prorated. We never lock you out the day before opening.",
      "bridge_note": "From {{events}} events a year, a subscription costs less.",
      "over_limit": "More than {{volunteers}} volunteers? Get in touch and we'll build the offer with you.",
      "picker": {
        "title": "Which event is it for?",
        "copy": "The {{tier}} package covers one event. Pick which one.",
        "cancel": "Cancel",
        "empty": "You have no event to cover yet.",
        "create_event": "Create an event",
        "error": "Your events could not be loaded right now.",
        "volunteers_one": "{{count}} volunteer",
        "volunteers_other": "{{count}} volunteers"
      }
    },
    "credit_packs": {
      "eyebrow": "Credit packs",
      "title": "{{events}} events, {{percent}}% off",
      "subtitle": "Running several events but don't want a subscription? Buy your credits up front: one credit = one event of the size you choose, to use whenever you like within {{months}} months.",
      "pack_label": "{{events}} × {{tier}}",
      "instead_of": "instead of €{{price}}",
      "per_event": "that's €{{price}} per event",
      "cta": "Buy this pack"
    },
    "plans": {
      "free": {
        "name": "Free",
        "tagline": "For your first event or a small association.",
        "cta": "Start for free",
        "features": [
          "1 active event",
          "Up to 10 volunteers",
          "2 team members",
          "5 contract signatures",
          "Roles & coverage timeline",
          "Automatic planning",
          "Volunteer portal",
          "English & French",
          "Community support"
        ]
      },
      "pro": {
        "name": "Pro",
        "tagline": "For organisers running events back to back all year.",
        "cta": "Start with Pro",
        "features_intro": "Everything in Free, plus:",
        "features": [
          "Unlimited events",
          "Up to 150 volunteers per event",
          "5 team members",
          "Unlimited contracts & e-signature",
          "Custom contract templates",
          "CSV volunteer import",
          "Automatic signing reminders",
          "Per-event access control",
          "Planning PDF export",
          "Email support"
        ]
      },
      "business": {
        "name": "Business",
        "tagline": "For very large volunteer operations and big teams.",
        "cta": "Start with Business",
        "features_intro": "Everything in Pro, plus:",
        "features": [
          "Unlimited volunteers per event",
          "Unlimited team members",
          "Guided onboarding",
          "Priority support"
        ]
      }
    },
    "compare": {
      "eyebrow": "Compare",
      "title": "Compare plans in detail",
      "feature_col": "Features",
      "unlimited": "Unlimited",
      "quota_labels": {
        "activeEvents": "Active events",
        "volunteersPerEvent": "Volunteers per event",
        "teamMembers": "Team members",
        "contractSignatures": "Contract signatures"
      },
      "rows": [
        {
          "label": "Roles & coverage timeline",
          "free": true,
          "pro": true,
          "business": true
        },
        {
          "label": "Automatic planning",
          "free": true,
          "pro": true,
          "business": true
        },
        {
          "label": "Volunteer portal",
          "free": true,
          "pro": true,
          "business": true
        },
        {
          "label": "Custom contract templates",
          "free": false,
          "pro": true,
          "business": true
        },
        {
          "label": "CSV volunteer import",
          "free": false,
          "pro": true,
          "business": true
        },
        {
          "label": "Automatic signing reminders",
          "free": false,
          "pro": true,
          "business": true
        },
        {
          "label": "Per-event access control",
          "free": false,
          "pro": true,
          "business": true
        },
        {
          "label": "Planning PDF export",
          "free": false,
          "pro": true,
          "business": true
        },
        {
          "label": "Guided onboarding",
          "free": false,
          "pro": false,
          "business": true
        },
        {
          "label": "Support",
          "free": "Community",
          "pro": "Email",
          "business": "Priority"
        }
      ]
    },
    "faq": {
      "eyebrow": "FAQ",
      "title": "Pricing questions",
      "items": [
        {
          "q": "What's the difference between paying per event and subscribing?",
          "a": "The product is exactly the same either way. An event package is paid once, for one specific event, and its price depends on the number of volunteers. A subscription gives you unlimited events all year. From three events a year, the subscription costs less."
        },
        {
          "q": "Can I really start for free?",
          "a": "Yes. The Free plan has no time limit and no credit card. It covers one active event, up to 10 volunteers, 2 team members and 5 contract signatures."
        },
        {
          "q": "What happens if I go over my volunteer limit?",
          "a": "Nothing gets blocked. Your event keeps running, the overage shows up in your workspace and we offer you the next size up, prorated. We never cut off access to a live event."
        },
        {
          "q": "How long do I keep access to an event I paid for once?",
          "a": "You keep full access for 6 months around the event date — plenty of time to prepare, run and close it. After that the event becomes a readable archive, and your PDF and CSV exports stay available."
        },
        {
          "q": "How do credit packs work?",
          "a": "A pack holds 3 credits at 20% off compared to buying them separately. One credit = one event of the size you choose, usable whenever you like within 24 months."
        },
        {
          "q": "Are prices excluding or including VAT?",
          "a": "Every price shown includes VAT. An invoice is issued automatically after each payment, whether it's an event, a pack or a subscription."
        },
        {
          "q": "What does yearly billing save me?",
          "a": "Yearly billing brings the Pro plan down from €29 to €24 per month, and the Business plan from €79 to €65 per month."
        },
        {
          "q": "Can I cancel anytime?",
          "a": "Yes. There is no commitment — cancel whenever you want. Your past events stay readable and exportable, and you fall back to the Free plan."
        }
      ]
    }
  },
  "publicGuestList": {
    "invalid": "This guest list link is invalid or has been revoked.",
    "subtitle": "Guest List",
    "summary": "{{guests}} guest(s) · {{people}} expected people",
    "empty": "No guests yet.",
    "guest_count": "{{n}} people",
    "present": "Present"
  },
  "guestList": {
    "search_placeholder": "Search a guest",
    "no_match": "No guest matches your search.",
    "guest_count": "{{n}} people",
    "present": "Present",
    "mark_present": "Check in"
  },
  "billing": {
    "checkout_failed": "Payment could not start. Please try again in a moment.",
    "back_to_app": "Back to my events",
    "see_plans": "See the plans",
    "success": {
      "badge": "Payment received",
      "title": "You're all set",
      "copy": "Your access opens as soon as the payment is confirmed, within seconds."
    },
    "cancelled": {
      "badge": "Payment cancelled",
      "title": "You have not been charged",
      "copy": "Your work is untouched. Pick up where you left off."
    },
    "manage_subscription": "Manage my subscription",
    "manage_hint": "Card, invoices and cancellation.",
    "upgrade_cta": "Move to a bigger plan"
  },
  "errors": {
    "codes": {
      "subscription_already_running": "This organisation already has an active subscription. Change or cancel it from the billing portal.",
      "event_already_covered": "This event is already covered by a package of the same size or larger.",
      "event_not_coverable": "This event is closed, so a package would unlock nothing on it.",
      "event_day_windows_overlap": "Two time windows overlap.",
      "event_phase_window_out_of_phase": "Setup must end before the event starts, and teardown must start after it ends.",
      "event_phase_too_many_days": "At most {{max}} setup days and {{max}} teardown days.",
      "event_show_window_out_of_range": "An event time window is outside the event dates.",
      "event_day_windows_required": "The new dates no longer match the time windows. Send the updated time windows with the new dates.",
      "role_outside_day_windows": "A role must fit entirely inside one time window (setup, event or teardown).",
      "role_outside_event_range": "A role must stay within the event dates.",
      "lineup_times_incomplete": "A set needs both a start and an end, or neither.",
      "lineup_times_order": "A set must end after it starts.",
      "lineup_status_transition": "A cancelled set can no longer change status.",
      "lineup_stage_name_taken": "A stage already has this name.",
      "lineup_amounts_forbidden": "You do not have access to amounts.",
      "artist_name_taken": "An artist already has this stage name.",
      "artist_in_lineup": "This artist is still on a line-up."
    }
  },
  "finances": {
    "eyebrow": "Finances",
    "title": "Volunteer payments",
    "subtitle": "Allowances and travel from signed contracts.",
    "filters_eyebrow": "Period",
    "filters_title": "Display",
    "year_label": "Year",
    "view_label": "View",
    "view": {
      "chart": "Chart",
      "table": "Table"
    },
    "stats": {
      "paid": "Paid so far",
      "to_pay": "Still to pay",
      "allowance": "Allowances",
      "travel": "Travel"
    },
    "series": {
      "allowance": "Allowance",
      "travel": "Travel",
      "allowance_paid": "Allowance paid",
      "allowance_to_pay": "Allowance to pay",
      "travel_paid": "Travel paid",
      "travel_to_pay": "Travel to pay"
    },
    "legend": {
      "to_pay": "Hatched: to pay",
      "cap": "Annual cap"
    },
    "monthly": {
      "title": "All volunteers",
      "subtitle": "By month of the event, in {{year}}.",
      "chart_label": "Payments by month",
      "column_label": "{{month}}: {{amount}}",
      "table_caption": "Volunteer payments by month"
    },
    "volunteers": {
      "title": "By volunteer",
      "subtitle": "Yearly total per volunteer.",
      "subtitle_cap": "Yearly total, against the {{cap}} cap.",
      "add_label": "Add a volunteer",
      "add_placeholder": "Search…",
      "select_all": "Show all",
      "clear": "Clear",
      "remove": "Remove {{name}}",
      "more": "+{{count}}",
      "empty_selection": "Pick one or more volunteers.",
      "chart_label": "Payments by volunteer",
      "row_label": "{{name}}: {{amount}}",
      "over_cap": "Cap exceeded",
      "deleted": "Account deleted",
      "table_caption": "Payments by volunteer",
      "subtitle_range": "From {{from}} to {{to}}.",
      "subtitle_range_cap": "From {{from}} to {{to}}, against the {{cap}} cap.",
      "subtitle_range_multi_year": "From {{from}} to {{to}}. The annual cap is not compared across years.",
      "empty_range": "No payments over this period.",
      "range_error": "Could not load this period."
    },
    "range": {
      "from": "From",
      "to": "To",
      "reset": "Whole year"
    },
    "table": {
      "month": "Month",
      "volunteer": "Volunteer",
      "total": "Total"
    },
    "empty_title": "No payments in {{year}}",
    "empty_hint": "Amounts appear as soon as a contract is signed.",
    "load_error": "Could not load the payments.",
    "note": "Mark a payment in the event accounting or in Contracts: both stay in sync."
  },
  "contractLanguage": {
    "label": "Contract language",
    "hint": "Contract, signing page and emails in this language.",
    "fr": "Français",
    "en": "English"
  }
}
